import { useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Pause, 
  Play, 
  Square, 
  Lock,
  Unlock,
  Volume2,
  MapPin,
  MessageCircle,
  Trash2,
  Pencil,
  Navigation,
  Frown,
  Meh,
  Smile,
  Zap,
  Flame,
  Footprints,
  Trophy,
  Maximize2,
  X,
  Layers,
  Map as MapIcon,
  Heart,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SAFE_AREA } from "@/utils/safeArea";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { Geolocation, Position } from "@capacitor/geolocation";
import { prepareMixPlayback, prepareBackgroundKeepAlive, setStraivNowPlaying } from "@/lib/straivNowPlaying";
import { CHAT_DEFER_CONTEXT_REFRESH_KEY } from "@/lib/runningCoachPlans";
import { startPhoneWorkoutSession, endPhoneWorkoutSession, signalWatchRunEnd, markSegmentOnWatch, pauseWatchRun, resumeWatchRun, pushPhoneDistanceToWatch, pushCoachGuideToWatch, isWatchAppInstalled, addWatchLiveMetricsListener, addWatchRunEndedListener, addWatchPauseListener, getWatchLiveMetrics, fetchLastCompletedWatchWorkoutForRun, clearLastCompletedWatchWorkout, withPluginTimeout, syncWatchProfile, type WatchLiveMetrics, type WatchCompletedWorkout } from "@/lib/straivWatch";
import { getStoredUnits } from "@/lib/units";
import { toast } from "sonner";
import { Analytics } from "@/lib/analytics";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { PlatformMap } from "@/components/PlatformMap";
import { ShareIcon } from "@/components/icons/ShareIcon";
import { useWorkouts } from "@/hooks/useWorkouts";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import type { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { RunLiveActivity } from "@/lib/runLiveActivity";
import type { GpsActivityKind } from "@/lib/gpsCardioWorkout";
import { exerciseNameForGpsActivity, workoutTypeForGpsActivity } from "@/lib/gpsCardioWorkout";
import {
  buildLocalRunningGuidance,
  buildWatchCoachGuidePayload,
  buildWorkoutCompleteSpeech,
  buildWorkoutCompleteGuidePayload,
  stabilizeWatchCoachGuidePayload,
  watchCoachGuideStructureKey,
  getCoachMinIntervalSeconds,
  parseRunningSettingsFromStorage,
  normalizeCoachPlanText,
  resolveLiveCoachTargetPace,
  resolveRacePacingForSnapshot,
  sanitizeCoachSpeechText,
  comparePaceToTarget,
  type RunningCoachGuideSettings,
  type RunningCoachSnapshot,
} from "@/lib/runningCoach";
import {
  buildHrZones,
  bucketHrZones,
  bucketsFromZoneSeconds,
  buildHrZoneVoiceCue,
  classifyBpm,
  formatHeartRateSummaryForCoach,
  normalizeHrSamples,
  normalizeHrTimestamp,
  type HrSample,
  type HrZoneId,
  type ZoneSecondsMap,
} from "@/lib/heartRateZones";
import { enrichSegmentsWithAvgHr, mergeWatchSegmentsIntoPhone, segmentWindowFromRecorded, segmentsCoverFullRun } from "@/lib/runSegmentHr";
import { buildCoachGuidePerformanceReport, type CoachGuidePerformanceReport } from "@/lib/coachGuideResults";
import { supabase } from "@/integrations/supabase/client";
import {
  readHeartRateSamplesInRange,
  readLatestHeartRateBpm,
  getHealthConnectedState,
  getHealthConnectedCacheSync,
} from "@/lib/appleHealth";
import { HeartRateZoneChart } from "@/components/HeartRateZoneChart";
import { RouteHrEffortChart } from "@/components/RouteHrEffortChart";
import { SummarySplitsSegmentsCarousel } from "@/components/RunSummaryCarousels";
import { buildZoneColoredRouteSegments } from "@/lib/routeHrZones";
import { formatPaceSecPerKm, formatElapsedSpeech, formatPaceSpeech, formatElapsedTime } from "@/lib/formatPace";
import {
  ageGpsPaceWindow,
  averagePaceSecondsPerKm,
  resolveCurrentLivePace,
} from "@/lib/livePace";
import { useUnitFormatters } from "@/hooks/useUnits";
import { formatDistanceValue, distanceUnitShort, isAudioDistanceInterval, audioDistanceIntervalMeters, splitLengthMeters, splitUnitShort, splitSecondsToPaceSecPerKm, formatPaceDisplay, paceUnitLabel } from "@/lib/units";
import {
  startNativeRunGpsTracking,
  stopNativeRunGpsTracking,
  drainNativeRunGpsPoints,
} from "@/lib/runLocationPermission";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import {
  parseRunPlan,
  getActivePhaseWithDistanceAnchors,
  describeActivePhaseForCoach,
  buildPlanIntroSpeech,
  buildPhaseTransitionCue,
  getPhaseTransitionCueKey,
  type ParsedRunStructure,
} from "@/lib/runPlanParser";
import {
  resetCoachVoiceSession,
  speakCoachCue,
  speakNativeNow,
  stopCoachVoice,
  prefetchCoachAudio,
  playCoachAudio,
} from "@/lib/naturalVoice";

// BackgroundGeolocation for iOS - enables GPS when phone is locked
const BG_GEO_NAMES = ["BackgroundGeolocation", "CapacitorCommunityBackgroundGeolocation"];
let _bgGeo: any = null;
const getBackgroundGeolocation = () => {
  if (_bgGeo) return _bgGeo;
  for (const name of BG_GEO_NAMES) {
    try {
      const p = registerPlugin(name) as any;
      if (p && typeof (p as any).addWatcher === "function") {
        _bgGeo = p;
        return _bgGeo;
      }
    } catch (_) {}
  }
  return null;
};

// Haversine formula to calculate distance between two GPS points (in meters)
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const formatTime = formatElapsedTime;

function PhoneGuideMetric({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-end justify-between gap-3 py-2.5 border-b border-border/25 last:border-0">
      <div
        className={`text-[2.15rem] leading-none font-mono font-bold tabular-nums ${
          accent ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground text-right leading-tight whitespace-pre-line pb-0.5">
        {label}
      </div>
    </div>
  );
}

const formatPace = formatPaceSecPerKm;

const formatSegmentDistance = (meters: number): string => {
  if (!meters || meters <= 0) return "0 m";
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
  return `${Math.round(meters)} m`;
};

// Map disabled temporarily due to react-leaflet bundling issues on iOS
// const RunningMap = lazy(() => import("@/components/running/RunningMap"));

interface GpsPoint {
  lat: number;
  lng: number;
  timestamp: number;
  accuracy?: number;
  /** Instantaneous speed in m/s when provided by GPS. */
  speedMps?: number;
}

const WATCH_METRICS_STALE_MS = 10_000;

type SummaryHeartRate = {
  zones: Parameters<typeof HeartRateZoneChart>[0]['zones'];
  buckets: Parameters<typeof HeartRateZoneChart>[0]['buckets'];
  maxHr?: number;
  maxHrSource?: 'measured' | 'estimated';
  avgBpm?: number | null;
  peakBpm?: number | null;
  targetZone?: import('@/lib/heartRateZones').HrZoneId;
};

function CarouselExpandSurface({
  onExpand,
  ariaLabel,
  carouselApi,
  className,
  children,
}: {
  onExpand: () => void;
  ariaLabel: string;
  carouselApi?: CarouselApi;
  className?: string;
  children: ReactNode;
}) {
  const touchRef = useRef({ x: 0, y: 0, t: 0, moved: false });
  const dragRef = useRef(false);

  useEffect(() => {
    if (!carouselApi) return;
    const onScroll = () => { dragRef.current = true; };
    const onSettle = () => { dragRef.current = false; };
    carouselApi.on('scroll', onScroll);
    carouselApi.on('settle', onSettle);
    return () => {
      carouselApi.off('scroll', onScroll);
      carouselApi.off('settle', onSettle);
    };
  }, [carouselApi]);

  return (
    <div
      className={cn('relative', className)}
      onTouchStart={(e) => {
        dragRef.current = false;
        touchRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          t: Date.now(),
          moved: false,
        };
      }}
      onTouchMove={(e) => {
        const dx = e.touches[0].clientX - touchRef.current.x;
        const dy = e.touches[0].clientY - touchRef.current.y;
        if (Math.hypot(dx, dy) > 14) touchRef.current.moved = true;
      }}
      onTouchEnd={() => {
        if (dragRef.current || touchRef.current.moved) return;
        if (Date.now() - touchRef.current.t > 400) return;
        onExpand();
      }}
      onClick={() => {
        if (dragRef.current || touchRef.current.moved) return;
        onExpand();
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExpand(); }
      }}
    >
      {children}
    </div>
  );
}

function SummaryMapHrCarousel({
  mapCenter,
  routeSegments,
  routePoints,
  routeLine,
  coloredRouteSegments,
  onExpandMap,
  onExpandHr,
  onExpandCombined,
  expandMapAria,
  expandHrAria,
  expandCombinedAria,
  heartRate,
}: {
  mapCenter: [number, number] | null;
  routeSegments: [number, number][][];
  routePoints: { lat: number; lng: number; timestamp?: number }[];
  routeLine: [number, number][];
  coloredRouteSegments: ReturnType<typeof buildZoneColoredRouteSegments>;
  onExpandMap: () => void;
  onExpandHr: () => void;
  onExpandCombined: () => void;
  expandMapAria: string;
  expandHrAria: string;
  expandCombinedAria: string;
  heartRate: SummaryHeartRate & { samples?: { timestamp: number; bpm: number }[] };
}) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [page, setPage] = useState(0);
  const pageCount = 3;

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setPage(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    onSelect();
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  const pageHints = ['Swipe for heart rate zones →', 'Swipe for route + HR effort →', ''];

  return (
    <div className="shrink-0 mb-2">
      <Carousel
        setApi={setCarouselApi}
        opts={{ align: 'start', loop: false, dragFree: false, duration: 28, skipSnaps: false }}
        className="w-full touch-pan-y"
      >
        <CarouselContent className="ml-0">
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandMap}
              ariaLabel={expandMapAria}
              className="rounded-lg overflow-hidden border border-border/30 h-44 min-h-[176px] cursor-pointer"
            >
              <PlatformMap
                key="summary-map-carousel"
                center={mapCenter}
                routePoints={routeLine}
                routeSegments={routeSegments}
                showUserMarker={false}
                showRoute
                showStartFinishMarkers
                showKmMarkers
                fitToRoute
                fitPaddingBottom={1.25}
                autoFollow={false}
                className="w-full h-full pointer-events-none"
                darkMode
                interactive={false}
                zoom={12}
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-5 w-5 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandHr}
              ariaLabel={expandHrAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-y-auto cursor-pointer"
            >
              <HeartRateZoneChart
                zones={heartRate.zones}
                buckets={heartRate.buckets}
                maxHr={heartRate.maxHr}
                maxHrSource={heartRate.maxHrSource}
                avgBpm={heartRate.avgBpm}
                peakBpm={heartRate.peakBpm}
                targetZone={heartRate.targetZone}
                className="text-xs"
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-4 w-4 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
          <CarouselItem className="pl-0 basis-full">
            <CarouselExpandSurface
              carouselApi={carouselApi}
              onExpand={onExpandCombined}
              ariaLabel={expandCombinedAria}
              className="rounded-lg border border-border/30 bg-muted/10 p-2 min-h-[148px] max-h-40 overflow-hidden cursor-pointer"
            >
              <div className="h-24 rounded overflow-hidden mb-2 border border-border/20">
                <PlatformMap
                  key="summary-combined-map"
                  center={mapCenter}
                  routePoints={routeLine}
                  routeSegments={coloredRouteSegments.length > 0 ? undefined : routeSegments}
                  coloredRouteSegments={coloredRouteSegments.length > 0 ? coloredRouteSegments : undefined}
                  showUserMarker={false}
                  showRoute
                  showStartFinishMarkers
                  showKmMarkers
                  fitToRoute
                  fitPaddingBottom={1.25}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode
                  interactive={false}
                  zoom={12}
                />
              </div>
              <RouteHrEffortChart
                routePoints={routePoints}
                hrSamples={heartRate.samples ?? []}
                zones={heartRate.zones}
              />
              <div className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-8 h-8 rounded-lg bg-black/70 pointer-events-none" aria-hidden>
                <Maximize2 className="h-4 w-4 text-white" />
              </div>
            </CarouselExpandSurface>
          </CarouselItem>
        </CarouselContent>
      </Carousel>
      <div className="flex justify-center gap-1.5 mt-1.5" aria-hidden>
        {Array.from({ length: pageCount }).map((_, i) => (
          <span key={i} className={cn('h-1 w-5 rounded-full transition-colors', page === i ? 'bg-primary' : 'bg-muted-foreground/30')} />
        ))}
      </div>
      {pageHints[page] ? (
        <p className="text-[10px] text-center text-muted-foreground mt-0.5">{pageHints[page]}</p>
      ) : null}
    </div>
  );
}

function sampleRoutePointsForSave<T extends { lat: number; lng: number; timestamp?: number }>(
  routePoints: T[],
  maxPoints = 120,
): T[] {
  if (routePoints.length <= maxPoints) return routePoints;
  const step = (routePoints.length - 1) / (maxPoints - 1);
  const sampled: T[] = [];
  for (let i = 0; i < maxPoints - 1; i++) {
    sampled.push(routePoints[Math.round(i * step)]);
  }
  sampled.push(routePoints[routePoints.length - 1]);
  return sampled;
}

function downsampleHrSamplesForSave(
  samples: { timestamp: number; bpm: number }[],
  maxPoints = 300,
): { timestamp: number; bpm: number }[] {
  if (samples.length <= maxPoints) return samples;
  const step = (samples.length - 1) / (maxPoints - 1);
  const out: { timestamp: number; bpm: number }[] = [];
  for (let i = 0; i < maxPoints; i++) {
    out.push(samples[Math.round(i * step)]);
  }
  return out;
}

function buildGpsRunExerciseNotesJson(args: {
  workoutSummary: Record<string, unknown> | null | undefined;
  kmSplits: { km: number; time: string; pace: string; isPartial?: boolean }[];
  enrichedRunSegments: Array<{
    index: number;
    durationSeconds: number;
    distanceMeters: number;
    time: string;
    avgHeartRateBpm?: number;
  }>;
  routePoints: Array<{ lat: number; lng: number; timestamp?: number }>;
  watchZoneSeconds: ZoneSecondsMap | null;
  pauseCount: number;
  feelingEmoji: string;
  autoSaved?: boolean;
  /** Slim payload for reliable first save (AI runs can be huge). */
  slim?: boolean;
}): string {
  const summary = args.workoutSummary;
  const heartRate = summary?.heartRate as Record<string, unknown> | null | undefined;
  const slim = args.slim === true;
  const hrMax = slim ? 180 : 300;
  const routeMax = slim ? 80 : 120;
  return JSON.stringify({
    pace: summary?.pace,
    splits: args.kmSplits,
    segments: args.enrichedRunSegments.map((s) => ({
      index: s.index,
      durationSeconds: s.durationSeconds,
      distanceMeters: s.distanceMeters,
      time: s.time,
      avgHeartRateBpm: s.avgHeartRateBpm,
    })),
    routePoints: args.routePoints.length > 0
      ? sampleRoutePointsForSave(args.routePoints, routeMax).map((p) => ({
          lat: p.lat,
          lng: p.lng,
          ...(p.timestamp != null ? { timestamp: p.timestamp } : {}),
        }))
      : [],
    zoneSeconds: args.watchZoneSeconds ?? undefined,
    // Full coach report can be large — include a compact version on slim saves.
    coachGuideReport: slim
      ? (summary?.coachGuideReport
          ? (() => {
              const report = summary.coachGuideReport as {
                planText?: unknown;
                overallSummary?: unknown;
                overall?: unknown;
                overallVerdict?: unknown;
                phases?: unknown[];
                blocks?: unknown[];
              };
              return {
                planText: report.planText,
                overallSummary: report.overallSummary ?? report.overall,
                overallVerdict: report.overallVerdict,
                phases: (report.phases ?? report.blocks ?? []).slice(0, 16),
              };
            })()
          : undefined)
      : summary?.coachGuideReport ?? undefined,
    pauseCount: args.pauseCount,
    avgSpeedKmh: summary?.averageSpeedKmh,
    feeling: args.feelingEmoji,
    autoSaved: args.autoSaved ?? false,
    heartRate: heartRate
      ? {
          maxHr: heartRate.maxHr,
          maxHrSource: heartRate.maxHrSource,
          targetZone: heartRate.targetZone,
          avgBpm: heartRate.avgBpm,
          minBpm: heartRate.minBpm,
          peakBpm: heartRate.peakBpm,
          zones: Array.isArray(heartRate.zones)
            ? heartRate.zones.map((z: { id: string; label: string; lowerBpm: number; upperBpm: number }) => ({
                id: z.id,
                label: z.label,
                lowerBpm: z.lowerBpm,
                upperBpm: z.upperBpm,
              }))
            : [],
          buckets: heartRate.buckets,
          hrSamples: Array.isArray(heartRate.samples)
            ? downsampleHrSamplesForSave(
                heartRate.samples.map((s: { timestamp: number; bpm: number }) => ({
                  timestamp: s.timestamp,
                  bpm: s.bpm,
                })),
                hrMax,
              )
            : [],
        }
      : null,
  });
}

async function resolveRunExerciseId(workoutId: string): Promise<string | null> {
  try {
    const { data } = await withPluginTimeout(
      supabase
        .from('exercises')
        .select('id')
        .eq('workout_id', workoutId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle() as unknown as Promise<{ data: { id: string } | null }>,
      8000,
      { data: null },
    );
    return data?.id ?? null;
  } catch {
    return null;
  }
}

export const RunningActive = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { units, formatKm, formatMeters, formatPace: formatPaceUnits, paceSuffix } = useUnitFormatters();
  const unitsRef = useRef(units);
  unitsRef.current = units;
  const { createWorkout, addExerciseToWorkout, deleteWorkout, updateWorkout, updateExercise } = useWorkouts();
  
  // Run state
  const [isRunning, setIsRunning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [pauseCount, setPauseCount] = useState(0);
  
  // Dialog states
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showRunSummary, setShowRunSummary] = useState(false);
  const [showAnalyzePrompt, setShowAnalyzePrompt] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExpandedMap, setShowExpandedMap] = useState(false);
  const [showExpandedHr, setShowExpandedHr] = useState(false);
  const [showExpandedCombined, setShowExpandedCombined] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBatteryPrompt, setShowBatteryPrompt] = useState(false);
  const [isSavingRun, setIsSavingRun] = useState(false);
  const [isStoppingRun, setIsStoppingRun] = useState(false);
  const runSaveInFlightRef = useRef(false);
  const savedWorkoutIdRef = useRef<string | null>(null);
  const savedExerciseIdRef = useRef<string | null>(null);
  /** Last known good distance/duration — never let Save overwrite these with zeros. */
  const savedRunMetricsRef = useRef<{ distanceKm: number; durationSec: number; calories: number } | null>(null);
  const runDiscardedRef = useRef(false);
  const autoSavePromiseRef = useRef<Promise<void> | null>(null);
  const autoSaveInFlightRef = useRef<Promise<void> | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [workoutToShare, setWorkoutToShare] = useState<ShareableWorkout | null>(null);
  
  // Map re-center state - use separate mapCenter to prevent constant refresh
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [isMapCentered, setIsMapCentered] = useState(true); // Track if map is centered on user
  
  // Track if stop was confirmed (to prevent "run resumed" being said)
  const stopConfirmedRef = useRef(false);
  const [workoutSummary, setWorkoutSummary] = useState<any>(null);
  const workoutSummaryRef = useRef<any>(null);
  const [runNotes, setRunNotes] = useState("");
  const [runTitle, setRunTitle] = useState("");
  const [feelingEmoji, setFeelingEmoji] = useState<string>("");
  const [kmSplits, setKmSplits] = useState<{ km: number; time: string; pace: string }[]>([]);
  const [runSegments, setRunSegments] = useState<{ index: number; durationSeconds: number; distanceMeters: number; time: string; avgHeartRateBpm?: number }[]>([]);
  const [segmentPopup, setSegmentPopup] = useState<{
    index: number;
    durationSeconds: number;
    distanceMeters: number;
    paceSecondsPerKm: number;
  } | null>(null);
  
  // GPS signal strength
  const [gpsSignal, setGpsSignal] = useState<'searching' | 'weak' | 'medium' | 'strong'>('searching');
  
  // Track distance for splits
  const lastSplitKmRef = useRef<number>(0);
  const splitTimesRef = useRef<{ km: number; elapsedSeconds: number }[]>([]);
  const runSegmentsRef = useRef<{ index: number; durationSeconds: number; distanceMeters: number; avgHeartRateBpm?: number }[]>([]);
  const markSegmentLockUntilRef = useRef(0);
  const segmentStartElapsedRef = useRef(0);
  const segmentStartDistanceRef = useRef(0);
  /** GPS window (timestamp ms, cumulative distance m) kept for ~90s so both the
   * ~2–3s time window and ~25–50m distance window pace calculations have history. */
  const gpsPaceWindowRef = useRef<{ t: number; dist: number }[]>([]);
  /** Latest GPS speed (m/s) for near-instant current pace. */
  const lastGpsSpeedMpsRef = useRef<number>(-1);
  const lastGpsSpeedAtRef = useRef<number>(0);
  /** Watch distance/elapsed snapshot for live pace between polls. */
  const watchPaceSnapshotRef = useRef<{ dist: number; elapsed: number } | null>(null);
  /** Ad-hoc pace from successive watch distance samples (fallback only). */
  const watchRollingPaceRef = useRef<number>(0);
  /** Watch AI Guide CURRENT PACE — same as Watch UI (`livePaceSecondsPerKm`). */
  const watchLivePaceRef = useRef<number>(0);
  /** Watch elapsed when distance last advanced — used to expire sticky pace while standing. */
  const watchLastMoveElapsedRef = useRef<number>(0);
  const watchElapsedSecondsRef = useRef<number>(0);
  /** True once watch elapsed belongs to THIS phone run (not a leftover session). */
  const watchClockReadyRef = useRef(false);
  const watchElapsedReceivedAtRef = useRef<number>(0);
  const watchStreamingRef = useRef(false);
  /** Watch elapsed/distance is truth only while live metrics are arriving. */
  const resolveLiveElapsedSeconds = useCallback(() => {
    const phone = elapsedSecondsRef.current;
    const watch = watchElapsedSecondsRef.current;
    const useWatch =
      watchClockReadyRef.current &&
      watch >= 0 &&
      (watchIsAuthoritativeRef.current ||
        (watchStreamingRef.current &&
          watchElapsedReceivedAtRef.current > 0 &&
          Date.now() - watchElapsedReceivedAtRef.current < 20_000));
    if (!useWatch) return phone;
    if (!isPausedRef.current && runActiveRef.current && !runEndedRef.current) {
      const sinceMs = Date.now() - watchElapsedReceivedAtRef.current;
      if (sinceMs > 0 && sinceMs < 20_000) {
        return Math.floor(watch + sinceMs / 1000);
      }
    }
    return watch;
  }, []);
  const resolveLiveDistanceMeters = useCallback(() => {
    const watchSnap = watchPaceSnapshotRef.current;
    const watchLive =
      watchIsAuthoritativeRef.current &&
      lastWatchMetricsAtRef.current > 0 &&
      Date.now() - lastWatchMetricsAtRef.current < WATCH_METRICS_STALE_MS;
    if (watchLive && watchSnap && watchSnap.dist > 0) {
      return watchSnap.dist;
    }
    return totalDistanceRef.current;
  }, []);
  const watchIsAuthoritativeRef = useRef(false);
  /** Phone GPS total — always accumulated. Displayed when the watch is absent or drops. */
  const phoneGpsDistanceRef = useRef(0);
  const lastWatchMetricsAtRef = useRef(0);
  const lastWatchDistanceChangeAtRef = useRef(0);
  const lastMergedWatchElapsedRef = useRef(-1);
  const lastMergedWatchDistanceRef = useRef(-1);

  const isWatchDistanceLive = useCallback(() => {
    return (
      watchIsAuthoritativeRef.current &&
      lastWatchMetricsAtRef.current > 0 &&
      Date.now() - lastWatchMetricsAtRef.current < WATCH_METRICS_STALE_MS
    );
  }, []);

  const dropStaleWatchAuthority = useCallback(() => {
    const now = Date.now();
    const fresh =
      lastWatchMetricsAtRef.current > 0 &&
      now - lastWatchMetricsAtRef.current < WATCH_METRICS_STALE_MS;
    const watchDist = watchPaceSnapshotRef.current?.dist ?? totalDistanceRef.current;
    const phoneAhead =
      phoneGpsDistanceRef.current > watchDist + 30 &&
      lastWatchDistanceChangeAtRef.current > 0 &&
      now - lastWatchDistanceChangeAtRef.current > 8_000;
    if (fresh && !phoneAhead) return;
    if (!watchIsAuthoritativeRef.current && !watchStreamingRef.current) return;

    const wasAuthoritative = watchIsAuthoritativeRef.current;
    watchIsAuthoritativeRef.current = false;
    watchStreamingRef.current = false;
    watchLivePaceRef.current = 0;
    watchRollingPaceRef.current = 0;
    if (!wasAuthoritative) return;

    const phoneDist = phoneGpsDistanceRef.current;
    const displayed = totalDistanceRef.current;
    if (phoneDist > displayed + 5) {
      totalDistanceRef.current = phoneDist;
      setTotalDistance(phoneDist);
      const splitMeters = splitLengthMeters(unitsRef.current);
      lastSplitKmRef.current = Math.max(
        lastSplitKmRef.current,
        Math.floor(phoneDist / splitMeters),
      );
    } else if (phoneDist < displayed) {
      phoneGpsDistanceRef.current = displayed;
    }
  }, []);
  const elapsedSecondsRef = useRef<number>(0); // Ref for accurate timing in callbacks
  
  // GPS data - routeSegments: each segment is a continuous run (no movement during pause)
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [routeSegments, setRouteSegments] = useState<GpsPoint[][]>([[]]);
  const [totalDistance, setTotalDistance] = useState(0); // in meters
  const totalDistanceRef = useRef(0);
  
  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number>(0);
  
  // GPS restart key - only used when a watcher is missing after foregrounding.
  const [gpsRestartKey, setGpsRestartKey] = useState(0);
  
  // GPS watch - watchId for standard Geolocation, bgWatcherId for BackgroundGeolocation
  const watchIdRef = useRef<string | null>(null);
  const bgWatcherIdRef = useRef<string | null>(null);
  const lastPositionRef = useRef<GpsPoint | null>(null);
  const locationPermissionConfirmedRef = useRef(false);
  const lastGpsUpdateRef = useRef<number>(Date.now());
  const runActiveRef = useRef(true);
  const isPausedRef = useRef(false);
  
  // Audio announcements
  const lastAnnouncementKmRef = useRef<number>(0);
  const lastDistanceIntervalRef = useRef<number>(0);
  const audioIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAnnouncedStartRef = useRef<boolean>(false);
  const announceStatsRef = useRef<() => void>(() => {});
  const lastManualAnnounceTimeRef = useRef<number>(0);
  const lastCoachAnnounceTimeRef = useRef<number>(0);
  const lastLiveCoachAtRef = useRef<number>(0);
  /** Invoked from interval + watch metrics so cadence survives iOS background timer throttling. */
  const maybeLiveCoachRef = useRef<(() => void) | null>(null);
  /** Last spoken coach text + when, so identical text can be re-spoken once a full tick has passed. */
  const lastCoachSpokenRef = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  const coachInFlightRef = useRef(false);
  const coachIntroAnnouncedRef = useRef(false);
  const coachIntroInProgressRef = useRef(false);
  const coachAudioReadyRef = useRef(false);
  const coachOpeningDoneRef = useRef(false);
  const coachPlanCompletedRef = useRef(false);
  const coachPlanCompleteAtRef = useRef<{ elapsed: number; distanceKm: number } | null>(null);
  const [coachArmed, setCoachArmed] = useState(false);

  /** Ignore stale HealthKit HR (e.g. old Watch samples without an active workout). */
  const LIVE_HR_MAX_AGE_MS = 120_000;
  const MIN_HR_SAMPLES_FOR_SUMMARY = 3;
  const MIN_HR_TRACKED_SECONDS = 20;

  const filterHrSamplesToRunWindow = useCallback((samples: HrSample[], runEndMs = Date.now()) => {
    const runStart = startTimeRef.current;
    if (!runStart) return [];
    return normalizeHrSamples(samples).filter(
      (s) =>
        s.timestamp >= runStart - 5_000 &&
        s.timestamp <= runEndMs + 5_000,
    );
  }, []);

  // Heart-rate samples streamed from Apple Health while the run is active.
  const hrSamplesRef = useRef<HrSample[]>([]);
  /** Zone dwell seconds from the watch — authoritative when phone HR samples are sparse. */
  const watchZoneSecondsRef = useRef<ZoneSecondsMap | null>(null);
  const lastHrAnnounceTimeRef = useRef<number>(0);
  const lastHrFetchEndRef = useRef<number>(0);
  const [currentBpm, setCurrentBpm] = useState<number | null>(null);
  const currentBpmRef = useRef<number | null>(null);
  // Seed from cached "connected" flag so the intro doesn't say "not connected"
  // during the brief moment before the native check returns.
  const [healthKitConnected, setHealthKitConnected] = useState<boolean>(() =>
    getHealthConnectedCacheSync(),
  );

  // Structured plan tracker: drives the "warm up → intervals → cooldown"
  // voice cues whenever Arnold (or the user) provided a multi-phase plan.
  const [activePhaseIndex, setActivePhaseIndex] = useState<number>(-1);
  const activePhaseIndexRef = useRef<number>(-1);
  const announcedEndCueForPhaseRef = useRef<Set<number>>(new Set());
  const announcedTransitionKeysRef = useRef<Set<string>>(new Set());
  const phaseStartedAtRef = useRef<number>(0);
  /** GPS distance (km) when each distance-bounded phase started — drives interval tracking. */
  const phaseStartDistanceKmByIndexRef = useRef<Map<number, number>>(new Map());
  const phaseEndDistanceKmByIndexRef = useRef<Map<number, number>>(new Map());
  /** Elapsed seconds when each phase started — drives block-specific pace. */
  const phaseStartElapsedSecondsByIndexRef = useRef<Map<number, number>>(new Map());
  /** Elapsed seconds when each distance-bounded phase first finished — drives recovery timing. */
  const phaseEndElapsedSecondsByIndexRef = useRef<Map<number, number>>(new Map());
  const lastResolvedPhaseIndexRef = useRef<number>(-1);
  const maxDistanceIntoBlockByPhaseRef = useRef<Map<number, number>>(new Map());
  /** Last structural AI-guide key pushed to watch — avoids flooding WCSession every meter. */
  const lastWatchGuideStructureKeyRef = useRef<string>('');
  const lastWatchGuidePushAtRef = useRef<number>(0);

  // Settings from RunningStart - load synchronously to prevent audio before settings load
  const [settings, setSettings] = useState<{
    activityType: string;
    audioStats: string;
    audioVoice?: 'female' | 'male';
    gpsActivity?: GpsActivityKind;
    coachGuide?: RunningCoachGuideSettings;
  }>(() => parseRunningSettingsFromStorage(localStorage.getItem('runningSettings')));

  const gpsKind: GpsActivityKind =
    settings.gpsActivity === 'walk' || settings.gpsActivity === 'hike'
      ? settings.gpsActivity
      : 'run';

  // Build the zone table once per (profile age) change.
  const ageYears = profile?.birth_date
    ? Math.max(10, new Date().getFullYear() - new Date(profile.birth_date).getFullYear())
    : null;
  const hrZoneSettings = useMemo(
    () => buildHrZones({ ageYears, maxHr: null }),
    [ageYears]
  );

  // Parse the coach plan into a structured timeline (warmup → intervals →
  // cooldown, with target zones/paces per phase). When the plan can't be
  // parsed we still get a single "free run" phase so the coach has something
  // to talk about.
  const planStructure: ParsedRunStructure = useMemo(() => {
    try {
      return parseRunPlan(normalizeCoachPlanText(settings.coachGuide?.plan));
    } catch (err) {
      console.warn('Failed to parse coach run plan:', err);
      return { phases: [], totalPlannedSeconds: 0, hasStructure: false };
    }
  }, [settings.coachGuide?.plan]);

  const coachModeActive = useMemo(() => {
    const plan = normalizeCoachPlanText(settings.coachGuide?.plan);
    return !!(settings.coachGuide?.enabled && plan.trim());
  }, [settings.coachGuide?.enabled, settings.coachGuide?.plan]);

  const [phoneRunView, setPhoneRunView] = useState<"guide" | "map">(
    coachModeActive ? "guide" : "map",
  );
  const showPhoneGuide = coachModeActive && phoneRunView === "guide";

  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const coachModeActiveRef = useRef(coachModeActive);
  coachModeActiveRef.current = coachModeActive;
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const planStructureRef = useRef(planStructure);
  planStructureRef.current = planStructure;
  const healthKitConnectedRef = useRef(healthKitConnected);
  healthKitConnectedRef.current = healthKitConnected;
  const runStartedSpeechRef = useRef<() => string>(() => '');

  // Phase-level target zone: derived from the active phase's `targetZone`.
  // Falls back to the plan-wide hint when the phase doesn't specify one.
  const planFallbackZone: HrZoneId | null = useMemo(() => {
    const plan = settings.coachGuide?.plan || '';
    const m = plan.match(/zone\s*([1-5])|\bz([1-5])\b/i);
    if (!m) return null;
    const num = parseInt(m[1] || m[2], 10);
    if (!Number.isFinite(num) || num < 1 || num > 5) return null;
    return (`z${num}` as HrZoneId);
  }, [settings.coachGuide?.plan]);

  const activePhase = activePhaseIndex >= 0 ? planStructure.phases[activePhaseIndex] : null;
  const targetZone: HrZoneId | null = activePhase?.targetZone || planFallbackZone || null;

  const gpsExerciseName = useMemo(() => exerciseNameForGpsActivity(gpsKind), [gpsKind]);
  const gpsWorkoutType = useMemo(() => workoutTypeForGpsActivity(gpsKind), [gpsKind]);

  const defaultTitleWithDate = useCallback(
    (dateStr: string) => {
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkDate', { date: dateStr });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeDate', { date: dateStr });
      return t('running.gpsActivity.titleRunDate', { date: dateStr });
    },
    [gpsKind, t]
  );

  const defaultTitleWithKm = useCallback(
    (km: number) => {
      const d = formatDistanceValue(km, units);
      const unit = distanceUnitShort(units);
      if (gpsKind === 'walk') return t('running.gpsActivity.titleWalkKm', { distance: `${d} ${unit}`, defaultValue: `Outdoor Walk - ${d} ${unit}` });
      if (gpsKind === 'hike') return t('running.gpsActivity.titleHikeKm', { distance: `${d} ${unit}`, defaultValue: `Outdoor Hike - ${d} ${unit}` });
      return t('running.gpsActivity.titleRunKm', { distance: `${d} ${unit}`, defaultValue: `Outdoor Run - ${d} ${unit}` });
    },
    [gpsKind, t, units]
  );

  const workoutDescriptionLine = useCallback(
    (km: number, timeLabel: string) => {
      if (gpsKind === 'walk')
        return t('running.gpsActivity.descWalk', { distance: km, duration: timeLabel });
      if (gpsKind === 'hike')
        return t('running.gpsActivity.descHike', { distance: km, duration: timeLabel });
      return t('running.gpsActivity.descRun', { distance: km, duration: timeLabel });
    },
    [gpsKind, t]
  );
  
  // Run starts immediately when screen loads (no countdown)
  const runActive = isRunning;
  const runEndedRef = useRef(false);
  const watchEndedStopRef = useRef(false);
  const pendingWatchWorkoutRef = useRef<WatchCompletedWorkout | null>(null);
  
  useEffect(() => {
    runActiveRef.current = runActive;
    isPausedRef.current = isPaused;
  }, [runActive, isPaused]);

  useEffect(() => {
    totalDistanceRef.current = totalDistance;
  }, [totalDistance]);

  useEffect(() => {
    currentBpmRef.current = currentBpm;
  }, [currentBpm]);

  // If iOS drops a watcher while the app is backgrounded, recreate it once on return.
  // Do not tear down healthy watchers on every foreground event: that caused repeated
  // permission checks and route gaps.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive || !runActiveRef.current || isPausedRef.current) return;
      const staleMs = Date.now() - lastGpsUpdateRef.current;
      const watchersMissing = !bgWatcherIdRef.current && !watchIdRef.current;
      if (watchersMissing || staleMs > 45_000) {
        console.warn('[GPS] Foreground recovery — restarting watchers', { watchersMissing, staleMs });
        setGpsRestartKey(k => k + 1);
      }
    });
    return () => { listener.then(l => l.remove()); };
  }, []);

  // Android: Prompt to disable battery optimization for reliable background tracking
  // (OPPO, Xiaomi, Huawei etc. aggressively suspend apps; disabling helps GPS + TTS continue)
  useEffect(() => {
    if (!runActive || Capacitor.getPlatform() !== 'android') return;
    const check = async () => {
      try {
        const dismissed = localStorage.getItem('straiv_battery_prompt_dismissed');
        if (dismissed === 'true') return;
        const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
        const { enabled } = await BatteryOptimization.isBatteryOptimizationEnabled();
        if (enabled) setShowBatteryPrompt(true);
      } catch (_) {}
    };
    void check();
  }, [runActive]);
  
  // Background persistence - save session periodically
  // Using refs to avoid stale closure issues
  const sessionDataRef = useRef<any>(null);
  
  // Flattened route for backward compat (storage, workout save) - memoized to prevent map flicker when typing
  const routePoints = useMemo(() => routeSegments.flat(), [routeSegments]);
  
  // Update session data ref whenever state changes
  useEffect(() => {
    sessionDataRef.current = {
      isRunning,
      isPaused,
      elapsedSeconds,
      totalDistance,
      routePoints,
      routeSegments,
      pauseCount,
      startTime: startTimeRef.current,
      pausedTime: pausedTimeRef.current,
      lastSplitKm: lastSplitKmRef.current,
      splitTimes: splitTimesRef.current,
      settings,
      timestamp: Date.now()
    };
  }, [isRunning, isPaused, elapsedSeconds, totalDistance, routeSegments, pauseCount, settings]);
  
  // Save session to localStorage periodically (only when run active, not during countdown)
  useEffect(() => {
    if (!runActive) return;
    
    const saveSession = () => {
      if (sessionDataRef.current) {
        sessionDataRef.current.timestamp = Date.now();
        try {
          localStorage.setItem('activeRunningSession', JSON.stringify(sessionDataRef.current));
        } catch (e) {
          console.warn('Failed to save running session:', e);
        }
      }
    };
    
    // Save immediately on start
    saveSession();
    
    // Then save every 5 seconds
    const saveInterval = setInterval(saveSession, 5000);
    return () => clearInterval(saveInterval);
  }, [runActive]);

  // Lock Screen + Dynamic Island Live Activity (iOS 16.2+, no-op elsewhere).
  // Starts as soon as we have an active run, pushes updates whenever stats
  // change (the wrapper throttles internally), and tears down on unmount so
  // the widget disappears reliably even on navigation/back-swipe.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    let cancelled = false;
    void RunLiveActivity.start().then(() => {
      if (cancelled) {
        void RunLiveActivity.end();
      }
    });

    return () => {
      cancelled = true;
      void RunLiveActivity.end();
    };
  }, [runActive]);

  // Push the latest stats into the Lock Screen / Dynamic Island. Pace is
  // whole-run average (matches the map stats bar). Pause/resume are
  // forced through with `flush` so the widget reacts instantly.
  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const paceSecondsPerKm = averagePaceSecondsPerKm(elapsedSeconds, totalDistance);

    const payload = {
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
      units,
    };

    void RunLiveActivity.flush(payload);
  }, [runActive, isPaused, units, elapsedSeconds, totalDistance]);

  useEffect(() => {
    if (!runActive) return;
    if (!RunLiveActivity.isSupported()) return;

    const paceSecondsPerKm = averagePaceSecondsPerKm(elapsedSeconds, totalDistance);

    void RunLiveActivity.update({
      elapsedSeconds,
      distanceMeters: totalDistance,
      paceSecondsPerKm,
      isPaused,
      units,
    });
  }, [elapsedSeconds, totalDistance, isPaused, runActive, units]);

  // Log screen view on mount
  useEffect(() => {
    Analytics.logScreenView('RunningActive');
  }, []);

  // iOS: native playback audio session so coach TTS continues when the phone is locked.
  // Mix with music first. Delay the silent keep-alive player until after the first
  // coach clip so it cannot steal Now Playing during the opening announcement.
  useEffect(() => {
    if (!runActive || Capacitor.getPlatform() !== 'ios') return;
    prepareMixPlayback();
    if (!coachModeActive) {
      prepareBackgroundKeepAlive();
    }
    const t = coachModeActive
      ? window.setTimeout(() => prepareBackgroundKeepAlive(), 25_000)
      : undefined;
    const sub = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) prepareBackgroundKeepAlive();
    });
    return () => {
      if (t) window.clearTimeout(t);
      sub.then((l) => l.remove());
    };
  }, [runActive, coachModeActive]);

  // iOS: launch Straiv Watch app + workout session for continuous HR.
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios' || !runActive) return;
    void clearLastCompletedWatchWorkout();
    void isWatchAppInstalled().then((installed) => {
      Analytics.watchAppDetected(installed);
    });
    void startPhoneWorkoutSession().then(() => {
      Analytics.watchRunSessionStarted();
    }).catch(() => undefined);
    setStraivNowPlaying(true, 'Straiv - Run in progress');
    return () => {
      // Only end the watch when this run screen actually stops — not when
      // user/profile identity hydrates and re-subscribes the effect.
      void signalWatchRunEnd()
        .catch(() => {})
        .finally(() => {
          void endPhoneWorkoutSession();
          setStraivNowPlaying(false);
        });
    };
  }, [runActive]);

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios' || !runActive || !user?.id) return;
    void syncWatchProfile(user.id, undefined, undefined, getStoredUnits()).catch(() => undefined);
  }, [runActive, user?.id]);

  const mergeWatchCompletedWorkout = useCallback((
    watchWorkout: WatchCompletedWorkout,
    stopElapsed: number,
    stopDistanceM: number,
  ): { stopElapsed: number; stopDistanceM: number } => {
    if (watchWorkout.segments?.length) {
      runSegmentsRef.current = mergeWatchSegmentsIntoPhone(
        runSegmentsRef.current,
        watchWorkout.segments.map((seg) => ({
          index: Number(seg.index),
          durationSeconds: seg.durationSeconds,
          distanceMeters: seg.distanceMeters,
          avgHeartRateBpm: seg.avgHeartRateBpm,
        })),
      );
    }
    if (watchWorkout.splits?.length) {
      splitTimesRef.current = watchWorkout.splits.map((s) => ({
        km: s.km,
        elapsedSeconds: s.elapsedSeconds,
      }));
    }
    if (watchWorkout.zoneSeconds) {
      watchZoneSecondsRef.current = { ...watchWorkout.zoneSeconds };
    }
    if (watchWorkout.hrSamples?.length) {
      const byTs = new Map<number, number>();
      for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
      for (const s of watchWorkout.hrSamples) {
        byTs.set(normalizeHrTimestamp(s.timestamp), s.bpm);
      }
      hrSamplesRef.current = [...byTs.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([timestamp, bpm]) => ({ timestamp, bpm }));
    }
    let elapsed = stopElapsed;
    let distanceM = stopDistanceM;
    if (watchWorkout.durationSeconds > elapsed) {
      elapsed = Math.round(watchWorkout.durationSeconds);
      elapsedSecondsRef.current = elapsed;
      setElapsedSeconds(elapsed);
    }
    // Watch is authoritative for distance only while it was live this run.
    if (watchIsAuthoritativeRef.current) {
      const watchDistance = watchWorkout.distanceMeters ?? 0;
      distanceM = watchDistance > 0 ? watchDistance : Math.max(stopDistanceM, phoneGpsDistanceRef.current);
      totalDistanceRef.current = distanceM;
      setTotalDistance(distanceM);
    } else {
      distanceM = Math.max(stopDistanceM, phoneGpsDistanceRef.current);
      totalDistanceRef.current = distanceM;
      setTotalDistance(distanceM);
    }
    return { stopElapsed: elapsed, stopDistanceM: distanceM };
  }, []);

  const mergeWatchLiveMetrics = useCallback((metrics: WatchLiveMetrics) => {
    if (metrics.currentHeartRate > 0) {
      setCurrentBpm(Math.round(metrics.currentHeartRate));
    } else if (metrics.avgHeartRateBpm > 0) {
      setCurrentBpm(Math.round(metrics.avgHeartRateBpm));
    }

    const runElapsed = elapsedSecondsRef.current;
    const watchElapsed = metrics.elapsedSeconds ?? 0;
    const watchDistance = metrics.distanceMeters ?? 0;
    const leftoverWatchClock =
      runElapsed < 20 && watchElapsed > runElapsed + 8;
    if (leftoverWatchClock) {
      watchClockReadyRef.current = false;
    } else if (watchElapsed <= 5 || Math.abs(watchElapsed - runElapsed) <= 3) {
      watchClockReadyRef.current = true;
    }
    const watchClockFrozen =
      !isPausedRef.current &&
      watchClockReadyRef.current &&
      runElapsed > watchElapsed + 15;
    const staleWatchSession =
      leftoverWatchClock ||
      watchClockFrozen ||
      !watchClockReadyRef.current ||
      (runElapsed < 45 &&
        (watchElapsed > runElapsed + 90 || watchDistance > totalDistanceRef.current + 200));
    if (staleWatchSession && watchIsAuthoritativeRef.current) {
      lastWatchMetricsAtRef.current = 0;
      dropStaleWatchAuthority();
    }

    if (watchElapsed >= 0 && !staleWatchSession) {
      watchElapsedSecondsRef.current = watchElapsed;
      watchElapsedReceivedAtRef.current = Date.now();
      watchStreamingRef.current = true;
      if (watchIsAuthoritativeRef.current || watchStreamingRef.current) {
        if (watchElapsed >= elapsedSecondsRef.current - 1) {
          const next = Math.max(elapsedSecondsRef.current, watchElapsed);
          if (next !== elapsedSecondsRef.current) {
            elapsedSecondsRef.current = next;
            setElapsedSeconds(next);
          }
        }
      }
    }

    // Prefer the watch's own CURRENT PACE (same number the Watch UI shows).
    const watchLivePace = metrics.livePaceSecondsPerKm ?? 0;
    if (!staleWatchSession && watchLivePace > 0 && watchLivePace <= 900) {
      watchLivePaceRef.current = watchLivePace;
      watchLastMoveElapsedRef.current = watchElapsed > 0 ? watchElapsed : watchLastMoveElapsedRef.current;
    } else if (
      watchLivePaceRef.current > 0 &&
      watchLastMoveElapsedRef.current > 0 &&
      watchElapsed - watchLastMoveElapsedRef.current >= 4
    ) {
      watchLivePaceRef.current = 0;
    }

    if (watchDistance > 0 && !staleWatchSession) {
      const prev = watchPaceSnapshotRef.current;
      // Fallback rolling pace from successive distance samples when watch live pace is absent.
      if (prev && watchDistance > prev.dist + 1.5 && watchElapsed > prev.elapsed + 0.8) {
        const dKm = (watchDistance - prev.dist) / 1000;
        const dSec = watchElapsed - prev.elapsed;
        if (dKm >= 0.002 && dSec >= 0.8 && dSec <= 5) {
          const pace = dSec / dKm;
          watchRollingPaceRef.current = pace > 0 && pace <= 900 ? pace : 0;
          if (watchLivePaceRef.current <= 0) {
            watchLastMoveElapsedRef.current = watchElapsed;
          }
        }
      } else if (prev && watchDistance <= prev.dist + 0.5 && watchElapsed > prev.elapsed + 3) {
        watchRollingPaceRef.current = 0;
      } else if (
        watchRollingPaceRef.current > 0 &&
        watchLastMoveElapsedRef.current > 0 &&
        watchElapsed - watchLastMoveElapsedRef.current >= 4
      ) {
        watchRollingPaceRef.current = 0;
      }
      watchPaceSnapshotRef.current = { dist: watchDistance, elapsed: watchElapsed };
    }

    if (
      coachModeActiveRef.current &&
      settingsRef.current.coachGuide?.enabled &&
      runActiveRef.current &&
      !isPausedRef.current &&
      !runEndedRef.current &&
      !coachPlanCompletedRef.current
    ) {
      const guide = stabilizeWatchCoachGuidePayload(
        buildWatchCoachGuidePayload({
          plan: settingsRef.current.coachGuide!.plan,
          structure: planStructureRef.current,
          elapsedSeconds: watchElapsed > 0 ? watchElapsed : elapsedSecondsRef.current,
          distanceKm: resolveLiveDistanceMeters() / 1000,
          phaseStartDistanceKmByIndex: phaseStartDistanceKmByIndexRef.current,
          phaseEndElapsedSecondsByIndex: phaseEndElapsedSecondsByIndexRef.current,
          lastPhaseIndexRef: lastResolvedPhaseIndexRef,
          phaseStartElapsedSecondsByIndex: phaseStartElapsedSecondsByIndexRef.current,
        }),
        lastResolvedPhaseIndexRef.current,
        maxDistanceIntoBlockByPhaseRef,
      );
      if (guide) {
        const key = watchCoachGuideStructureKey(guide);
        const now = Date.now();
        // Only push on phase/metadata change — never on every live-metrics tick.
        if (key !== lastWatchGuideStructureKeyRef.current) {
          lastWatchGuideStructureKeyRef.current = key;
          lastWatchGuidePushAtRef.current = now;
          void pushCoachGuideToWatch(guide);
        }
      }
      // Watch metrics keep arriving while the screen is locked — drive live cadence here.
      maybeLiveCoachRef.current?.();
    }

    // Watch is the source of truth for distance only while it is streaming live.
    // Identical cached snapshots (watch died, App Group still has last JSON) must
    // not keep refreshing "last seen" or distance stays frozen forever.
    const sameSnapshot =
      lastMergedWatchElapsedRef.current === watchElapsed &&
      lastMergedWatchDistanceRef.current === watchDistance;
    if (!staleWatchSession) {
      if (!sameSnapshot || isPausedRef.current) {
        lastWatchMetricsAtRef.current = Date.now();
      }
      if (!sameSnapshot) {
        lastMergedWatchElapsedRef.current = watchElapsed;
        lastMergedWatchDistanceRef.current = watchDistance;
      }
    }
    if (
      !staleWatchSession &&
      metrics.distanceMeters != null &&
      metrics.distanceMeters > 0
    ) {
      if (!watchIsAuthoritativeRef.current) {
        watchIsAuthoritativeRef.current = true;
        Analytics.watchMetricsAuthoritative();
      }
      const prevWatchDist = watchPaceSnapshotRef.current?.dist ?? 0;
      if (metrics.distanceMeters > prevWatchDist + 1) {
        lastWatchDistanceChangeAtRef.current = Date.now();
      }
      totalDistanceRef.current = metrics.distanceMeters;
      setTotalDistance(metrics.distanceMeters);
    }
    if (metrics.zoneSeconds) {
      watchZoneSecondsRef.current = { ...metrics.zoneSeconds };
    }
    if (metrics.hrSamples?.length) {
      const byTs = new Map<number, number>();
      for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
      for (const s of metrics.hrSamples) {
        byTs.set(normalizeHrTimestamp(s.timestamp), s.bpm);
      }
      hrSamplesRef.current = [...byTs.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([timestamp, bpm]) => ({ timestamp, bpm }));
    }
    if (metrics.splits?.length && !staleWatchSession) {
      let prevSeconds = 0;
      const splits = metrics.splits.map((split) => {
        const splitSeconds = split.elapsedSeconds - prevSeconds;
        prevSeconds = split.elapsedSeconds;
        return {
          km: split.km,
          time: formatTime(split.elapsedSeconds),
          pace: formatPace(split.paceSecondsPerKm || splitSeconds),
        };
      });
      splitTimesRef.current = metrics.splits.map((s) => ({
        km: s.km,
        elapsedSeconds: s.elapsedSeconds,
      }));
      setKmSplits(splits);
    }
    if (metrics.segments?.length) {
      const watchSegs = metrics.segments.map((seg) => ({
        index: Number(seg.index),
        durationSeconds: seg.durationSeconds,
        distanceMeters: seg.distanceMeters,
        avgHeartRateBpm: seg.avgHeartRateBpm,
      }));
      const echoWindow = Date.now() < markSegmentLockUntilRef.current;
      runSegmentsRef.current = mergeWatchSegmentsIntoPhone(runSegmentsRef.current, watchSegs, {
        appendNew: !echoWindow && watchClockReadyRef.current,
      });
      setRunSegments(
        runSegmentsRef.current.map((seg) => ({
          ...seg,
          time: formatTime(Math.round(seg.durationSeconds)),
        })),
      );
    }
  }, [dropStaleWatchAuthority, resolveLiveDistanceMeters]);

  // Background audio keep-alive: prevents WebView/JS from suspending when screen is off.
  // iOS: native silent keep-alive only (skips when Spotify/Music is playing — see SceneDelegate).
  // Do NOT play an HTML silent WAV on iOS — that pauses user music.
  // Android: silent WAV loop.
  useEffect(() => {
    if (!runActive || !Capacitor.isNativePlatform()) return;
    const isIOS = Capacitor.getPlatform() === 'ios';
    if (isIOS) {
      prepareBackgroundKeepAlive();
      const id = window.setInterval(() => {
        if (runActiveRef.current && !runEndedRef.current) prepareBackgroundKeepAlive();
      }, 20_000);
      return () => window.clearInterval(id);
    }
    let audio: HTMLAudioElement | null = null;
    let blobUrl: string | null = null;
    let cancelled = false;
    let pauseHandler: (() => void) | null = null;
    const startAudio = () => {
      if (cancelled) return;
      if (audio && !audio.paused) return;
      if (audio && audio.paused) {
        const p = audio.play();
        if (p?.catch) p.catch(() => { if (!cancelled) setTimeout(startAudio, 800); });
        return;
      }
      try {
        const sampleRate = 44100;
        const numChannels = 1;
        const bitsPerSample = 16;
        const duration = 1;
        const dataSize = sampleRate * numChannels * (bitsPerSample / 8) * duration;
        const buffer = new ArrayBuffer(44 + dataSize);
        const view = new DataView(buffer);
        const writeStr = (offset: number, str: string) => {
          for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
        };
        writeStr(0, 'RIFF');
        view.setUint32(4, 36 + dataSize, true);
        writeStr(8, 'WAVE');
        writeStr(12, 'fmt ');
        view.setUint32(16, 16, true);
        view.setUint16(20, 1, true);
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
        view.setUint16(32, numChannels * (bitsPerSample / 8), true);
        view.setUint16(34, bitsPerSample, true);
        writeStr(36, 'data');
        view.setUint32(40, dataSize, true);
        view.setInt16(44, 1, true);
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        blobUrl = URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
        audio = new Audio(blobUrl);
        audio.loop = true;
        audio.volume = 0.01;
        const tryPlay = () => {
          if (!audio || cancelled) return;
          const p = audio.play();
          if (p?.catch) {
            p.catch((e: unknown) => {
              if (!cancelled) {
                console.warn('[Run] Background audio play failed, retrying in 800ms:', e);
                setTimeout(tryPlay, 800);
              }
            });
          }
        };
        tryPlay();
        pauseHandler = () => {
          if (cancelled || !audio) return;
          if (audio.ended) return;
          setTimeout(() => {
            if (cancelled || !audio) return;
            if (audio.paused) void audio.play().catch(() => undefined);
          }, 200);
        };
        audio.addEventListener('pause', pauseHandler);
      } catch (e) {
        console.warn('[Run] Background audio setup failed:', e);
      }
    };
    startAudio();
    return () => {
      cancelled = true;
      if (audio && pauseHandler) audio.removeEventListener('pause', pauseHandler);
      try {
        audio?.pause();
      } catch {}
      audio = null;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [runActive]);

  // Keyboard height for Run Summary - keep Notes visible above keyboard on iOS
  const keyboardListenersRef = useRef<{ remove: () => Promise<void> }[]>([]);
  useEffect(() => {
    if (!showRunSummary || !Capacitor.isNativePlatform()) return;
    let cancelled = false;
    const isIOS = Capacitor.getPlatform() === 'ios';
    const showEvent = isIOS ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = isIOS ? 'keyboardWillHide' : 'keyboardDidHide';
    void Promise.all([
      Keyboard.addListener(showEvent as 'keyboardWillShow', (info: { keyboardHeight: number }) => {
        if (!cancelled) setKeyboardHeight(info.keyboardHeight);
      }),
      Keyboard.addListener(hideEvent as 'keyboardWillHide', () => {
        if (!cancelled) setKeyboardHeight(0);
      }),
    ]).then(([show, hide]) => {
      if (!cancelled) {
        keyboardListenersRef.current = [show, hide];
      } else {
        show.remove();
        hide.remove();
      }
    });
    return () => {
      cancelled = true;
      setKeyboardHeight(0);
      keyboardListenersRef.current.forEach((l) => l.remove());
      keyboardListenersRef.current = [];
    };
  }, [showRunSummary]);

  
  // Restore session from storage on mount (for background persistence)
  useEffect(() => {
    const launchStartIso = (settings as { startTime?: string }).startTime;
    const launchStartMs = launchStartIso ? Date.parse(launchStartIso) : NaN;
    const savedSession = localStorage.getItem('activeRunningSession');
    let restored = false;
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        const sessionLaunch = session.settings?.startTime as string | undefined;
        const sameLaunch =
          !!launchStartIso &&
          (sessionLaunch === launchStartIso ||
            (Number.isFinite(launchStartMs) &&
              Math.abs((session.startTime || 0) - launchStartMs) < 8000));
        if (sameLaunch && Date.now() - session.timestamp < 2 * 60 * 60 * 1000) {
          console.log('[Run] Restoring session from background:', session);
          const segments = session.routeSegments || (session.routePoints?.length ? [session.routePoints] : [[]]);
          setRouteSegments(segments);
          const pts = segments.flat();
          if (pts.length > 0) {
            const last = pts[pts.length - 1];
            setCurrentPosition([last.lat, last.lng]);
            lastPositionRef.current = last;
          }
          setElapsedSeconds(session.elapsedSeconds || 0);
          elapsedSecondsRef.current = session.elapsedSeconds || 0;
          const restoredDistance = session.totalDistance || 0;
          setTotalDistance(restoredDistance);
          totalDistanceRef.current = restoredDistance;
          phoneGpsDistanceRef.current = restoredDistance;
          setPauseCount(session.pauseCount || 0);
          setIsPaused(session.isPaused || false);
          startTimeRef.current = session.startTime || Date.now();
          pausedTimeRef.current = session.pausedTime || 0;
          lastSplitKmRef.current = session.lastSplitKm || 0;
          splitTimesRef.current = session.splitTimes || [];
          if (session.settings) setSettings(session.settings);
          Analytics.runningSessionRestored(session.elapsedSeconds || 0, session.totalDistance || 0);
          restored = true;
        } else {
          localStorage.removeItem('activeRunningSession');
          if (session.timestamp) {
            Analytics.runningSessionExpired(Math.round((Date.now() - session.timestamp) / 60000));
          }
        }
      } catch (e) {
        console.warn('Failed to restore running session:', e);
      }
    }
    if (!restored) {
      startTimeRef.current = Number.isFinite(launchStartMs) ? launchStartMs : Date.now();
      pausedTimeRef.current = 0;
      elapsedSecondsRef.current = 0;
      setElapsedSeconds(0);
      setTotalDistance(0);
      totalDistanceRef.current = 0;
    }
    runSegmentsRef.current = [];
    setRunSegments([]);
    markSegmentLockUntilRef.current = 0;
    runDiscardedRef.current = false;
    savedWorkoutIdRef.current = null;
    savedExerciseIdRef.current = null;
    savedRunMetricsRef.current = null;
    autoSavePromiseRef.current = null;
    hrSamplesRef.current = [];
    watchZoneSecondsRef.current = null;
    splitTimesRef.current = [];
    setKmSplits([]);
    lastSplitKmRef.current = 0;
    watchPaceSnapshotRef.current = null;
    watchRollingPaceRef.current = 0;
    watchLivePaceRef.current = 0;
    watchLastMoveElapsedRef.current = 0;
    coachPlanCompletedRef.current = false;
    coachPlanCompleteAtRef.current = null;
    watchElapsedSecondsRef.current = 0;
    watchElapsedReceivedAtRef.current = 0;
    watchStreamingRef.current = false;
    watchIsAuthoritativeRef.current = false;
    watchClockReadyRef.current = false;
    lastWatchMetricsAtRef.current = 0;
    lastWatchDistanceChangeAtRef.current = 0;
    lastMergedWatchElapsedRef.current = -1;
    lastMergedWatchDistanceRef.current = -1;
    if (!restored) {
      phoneGpsDistanceRef.current = 0;
    }
    gpsPaceWindowRef.current = [];
    lastGpsSpeedMpsRef.current = -1;
    lastGpsSpeedAtRef.current = 0;
    phaseStartDistanceKmByIndexRef.current.clear();
    phaseEndDistanceKmByIndexRef.current.clear();
    phaseStartElapsedSecondsByIndexRef.current.clear();
    phaseEndElapsedSecondsByIndexRef.current.clear();
    lastResolvedPhaseIndexRef.current = -1;
    maxDistanceIntoBlockByPhaseRef.current.clear();
  }, []);

  // Routes announcements to the right TTS engine:
  //  - Coach mode  → cloud TTS (OpenAI/ElevenLabs natural voice)
  //  - Normal run  → on-device voice (instant, no cold-start delay)
  type AnnounceKind = 'coach' | 'stats' | 'system';
  const announce = useCallback(async (
    text: string,
    options?: { duckOthers?: boolean; force?: boolean; kind?: AnnounceKind; priority?: boolean; interrupt?: boolean },
  ) => {
    if (!audioEnabled || typeof window === 'undefined') return;

    const kind = options?.kind ?? 'stats';
    if (coachModeActive && kind === 'stats') return;
    if (!coachModeActive && kind === 'coach') return;
    if (!coachModeActive && settings.audioStats === 'off' && kind === 'stats' && !options?.force) return;

    const sanitized = sanitizeCoachSpeechText(
      String(text || '')
        .replace(/[<>&"'`{}\[\]\\|@#$%^*+=~:;\/]/g, ' ')
        .replace(/[''\u2018\u2019\u201C\u201D]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    );
    if (!sanitized) return;

    const voice = (settings.audioVoice || 'female') as 'male' | 'female';

    try {
      // Coach mode uses queued cloud TTS. Normal run uses on-device TTS
      // directly so cues are instant.
      if (coachModeActive) {
        if (coachIntroInProgressRef.current && kind === 'coach' && !options?.interrupt) return;
        await speakCoachCue(sanitized, {
          voice,
          useNatural: true,
          priority: options?.priority,
          interrupt: options?.interrupt,
        });
      } else {
        await speakNativeNow(sanitized, voice);
      }
    } catch (err) {
      console.warn('[Audio] announce failed:', err);
    }
  }, [audioEnabled, settings.audioStats, settings.audioVoice, coachModeActive]);
  
  // Helper to convert time to natural speech (e.g., "2 minutes and 30 seconds")
  // Always round to whole seconds — fractional watch elapsed was spoken as "26.49462 seconds".
  const formatTimeForSpeech = useCallback((totalSeconds: number): string => {
    return formatElapsedSpeech(totalSeconds);
  }, []);
  
  // Announce stats with natural, conversational speech
  const announceStats = useCallback(() => {
    const distanceKm = totalDistance / 1000;
    const paceSeconds = elapsedSeconds > 0 && distanceKm > 0 
      ? elapsedSeconds / distanceKm 
      : 0;
    
    // Natural distance — max 2 decimal places (TTS reads "1.23 kilometers" cleanly).
    let distanceText: string;
    if (units === 'imperial') {
      const miles = totalDistance / 1609.344;
      if (miles >= 1) {
        const rounded = Math.round(miles * 100) / 100;
        distanceText = `${rounded} mile${rounded === 1 ? '' : 's'}`;
      } else {
        const yards = Math.round(totalDistance * 1.09361);
        distanceText = `${yards} yards`;
      }
    } else if (distanceKm >= 1) {
      const rounded = Math.round(distanceKm * 100) / 100;
      distanceText = `${rounded} kilometer${rounded === 1 ? '' : 's'}`;
    } else {
      distanceText = `${Math.round(totalDistance)} meters`;
    }
    
    // Natural time announcement (e.g., "12 minutes and 30 seconds")
    const timeText = formatTimeForSpeech(Math.round(elapsedSeconds));
    
    // Pace — whole seconds only (e.g. "4 minutes and 15 seconds per kilometer")
    const paceUnitSpeech = units === 'imperial' ? 'per mile' : 'per kilometer';
    let announcement = `You have covered ${distanceText} in ${timeText}`;
    if (paceSeconds > 0 && isFinite(paceSeconds)) {
      const paceSpeech = formatPaceSpeech(Math.round(paceSeconds), units).replace(
        / per (kilometer|mile)$/,
        '',
      );
      announcement += `. Average pace ${paceSpeech} ${paceUnitSpeech}`;
    }
    announcement += '.';
    announce(announcement, { kind: 'stats', duckOthers: true });
  }, [totalDistance, elapsedSeconds, announce, formatTimeForSpeech, units]);
  
  const isTimeBasedInterval = ['1min', '5min', '10min'].includes(settings.audioStats);
  const isDistanceBasedInterval = isAudioDistanceInterval(settings.audioStats);
  const distanceIntervalMeters = audioDistanceIntervalMeters(settings.audioStats);

  // Keep ref updated so interval callback always has latest (avoids resetting interval every second)
  announceStatsRef.current = announceStats;

  // Per-split milestone announcements (1 km or 1 mi) — disabled when the user
  // picked a stats interval (time-based OR distance-based).
  useEffect(() => {
    if (coachModeActive) return;
    if (isTimeBasedInterval || isDistanceBasedInterval || settings.audioStats === 'off') return;
    const splitMeters = splitLengthMeters(units);
    const currentSplit = Math.floor(totalDistance / splitMeters);
    if (currentSplit > lastAnnouncementKmRef.current && currentSplit > 0) {
      lastAnnouncementKmRef.current = currentSplit;
      
      // Calculate split pace for this km/mi
      const currentElapsed = elapsedSecondsRef.current;
      const prevSplit = splitTimesRef.current.find(s => s.km === currentSplit - 1);
      const splitTime = prevSplit ? currentElapsed - prevSplit.elapsedSeconds : currentElapsed;
      const splitMins = Math.floor(splitTime / 60);
      const splitSecs = splitTime % 60;
      
      // Log kilometer/mile milestone
      Analytics.runningKilometerCompleted(currentSplit, splitTime, currentElapsed);
      Analytics.runningAudioAnnouncementPlayed('km_milestone');
      
      const unitWord = units === 'imperial'
        ? (currentSplit === 1 ? 'mile' : 'miles')
        : (currentSplit === 1 ? 'kilometer' : 'kilometers');
      let message = `${currentSplit} ${unitWord} done`;
      if (splitTime > 0 && splitTime < 1800) {
        if (splitMins > 0) {
          message += ` in ${splitMins} minute${splitMins !== 1 ? 's' : ''}${splitSecs > 0 ? ` and ${splitSecs} seconds` : ''}`;
        } else {
          message += ` in ${splitSecs} seconds`;
        }
      }
      message += '.';
      announce(message, { kind: 'stats', duckOthers: true });
    }
  }, [totalDistance, announce, isTimeBasedInterval, isDistanceBasedInterval, settings.audioStats, coachModeActive, units]);
  
  // Timed audio announcements (every 1/5/10 min) - only when time-based interval
  // Uses ref for announceStats so interval is NOT reset every second (elapsedSeconds changes)
  useEffect(() => {
    if (coachModeActive) return;
    if (!isTimeBasedInterval || settings.audioStats === 'off' || !audioEnabled || isPaused || !runActive) {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
        audioIntervalRef.current = null;
      }
      return;
    }
    
    const intervalMinutes = parseInt(settings.audioStats, 10) || 5;
    const intervalMs = intervalMinutes * 60 * 1000;
    
    audioIntervalRef.current = setInterval(() => {
      if (runActive && !isPaused) {
        // Skip if user recently triggered manual announce (prevents cutting off button-triggered speech)
        if (Date.now() - lastManualAnnounceTimeRef.current < 90000) return;
        announceStatsRef.current();
      }
    }, intervalMs);
    
    return () => {
      if (audioIntervalRef.current) {
        clearInterval(audioIntervalRef.current);
      }
    };
  }, [settings.audioStats, audioEnabled, isPaused, runActive, isTimeBasedInterval, coachModeActive]);
  
  // Distance-based audio announcements (every 500m / 1km / 5km)
  useEffect(() => {
    if (coachModeActive) return;
    if (!isDistanceBasedInterval || distanceIntervalMeters <= 0 || !audioEnabled || isPaused || !runActive) return;
    while (totalDistance >= lastDistanceIntervalRef.current + distanceIntervalMeters) {
      lastDistanceIntervalRef.current += distanceIntervalMeters;
      announceStats();
      Analytics.runningAudioAnnouncementPlayed('stats');
    }
  }, [totalDistance, isDistanceBasedInterval, distanceIntervalMeters, audioEnabled, isPaused, runActive, announceStats, coachModeActive]);

  const runStartedSpeech = useCallback(() => {
    return t(
      gpsKind === 'walk'
        ? 'running.gpsActivity.sessionStarted_walk'
        : gpsKind === 'hike'
          ? 'running.gpsActivity.sessionStarted_hike'
          : 'running.gpsActivity.sessionStarted_run',
    );
  }, [t, gpsKind]);
  runStartedSpeechRef.current = runStartedSpeech;

  // Clear any stale "TTS unavailable" cache when a coached run starts.
  useEffect(() => {
    if (coachModeActive) resetCoachVoiceSession();
  }, [coachModeActive]);

  // Push AI guide to Apple Watch as soon as coach mode starts (independent of audio).
  useEffect(() => {
    if (!coachModeActive || !settings.coachGuide?.enabled || !settings.coachGuide.plan.trim()) return;
    if (!runActive || isPaused) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

    const pushGuideNow = (force = false) => {
      if (!runActiveRef.current || isPausedRef.current || runEndedRef.current) return;
      if (coachPlanCompletedRef.current) {
        const snap = coachPlanCompleteAtRef.current;
        if (snap) {
          const complete = buildWorkoutCompleteGuidePayload(snap.elapsed, snap.distanceKm);
          const key = watchCoachGuideStructureKey(complete);
          if (force || key !== lastWatchGuideStructureKeyRef.current) {
            lastWatchGuideStructureKeyRef.current = key;
            lastWatchGuidePushAtRef.current = Date.now();
            void pushCoachGuideToWatch(complete);
          }
        }
        return;
      }
      const elapsed = resolveLiveElapsedSeconds();
      const guide = stabilizeWatchCoachGuidePayload(
        buildWatchCoachGuidePayload({
          plan: settings.coachGuide!.plan,
          structure: planStructureRef.current,
          elapsedSeconds: elapsed,
          distanceKm: resolveLiveDistanceMeters() / 1000,
          phaseStartDistanceKmByIndex: phaseStartDistanceKmByIndexRef.current,
          phaseEndElapsedSecondsByIndex: phaseEndElapsedSecondsByIndexRef.current,
          lastPhaseIndexRef: lastResolvedPhaseIndexRef,
          phaseStartElapsedSecondsByIndex: phaseStartElapsedSecondsByIndexRef.current,
        }),
        lastResolvedPhaseIndexRef.current,
        maxDistanceIntoBlockByPhaseRef,
      );
      if (!guide) return;
      const key = watchCoachGuideStructureKey(guide);
      const now = Date.now();
      // Structural change, or rare heartbeat so a missed sendMessage still lands.
      const heartbeatDue = now - lastWatchGuidePushAtRef.current >= 8_000;
      if (!force && key === lastWatchGuideStructureKeyRef.current && !heartbeatDue) return;
      lastWatchGuideStructureKeyRef.current = key;
      lastWatchGuidePushAtRef.current = now;
      void pushCoachGuideToWatch(guide);
    };

    pushGuideNow(true);
    // Slow poll: watch derives km locally; phone only refreshes phase metadata.
    const id = window.setInterval(() => pushGuideNow(false), 2000);
    return () => window.clearInterval(id);
  }, [
    coachModeActive,
    settings.coachGuide?.enabled,
    settings.coachGuide?.plan,
    runActive,
    isPaused,
    elapsedSeconds,
    totalDistance,
    activePhaseIndex,
    resolveLiveElapsedSeconds,
    resolveLiveDistanceMeters,
  ]);

  // Structured plan finished — celebrate once on watch + coach voice.
  useEffect(() => {
    if (!coachModeActive || !runActive || isPaused || coachPlanCompletedRef.current) return;
    if (!coachArmed || planStructure.phases.length === 0) return;

    const elapsed = resolveLiveElapsedSeconds();
    const distanceKm = resolveLiveDistanceMeters() / 1000;
    const active = getActivePhaseWithDistanceAnchors(
      planStructure,
      elapsed,
      distanceKm,
      phaseStartDistanceKmByIndexRef.current,
      phaseEndElapsedSecondsByIndexRef.current,
      lastResolvedPhaseIndexRef,
      phaseStartElapsedSecondsByIndexRef.current,
    );
    if (!active) return;

    const lastPhase = planStructure.phases[planStructure.phases.length - 1];
    if (active.phase.index !== lastPhase.index) return;

    const planDone =
      (active.remainingSeconds != null && active.remainingSeconds <= 0) ||
      (active.remainingDistanceKm != null && active.remainingDistanceKm <= 0.02);
    if (!planDone) return;

    coachPlanCompletedRef.current = true;
    coachPlanCompleteAtRef.current = { elapsed, distanceKm };
    phaseEndDistanceKmByIndexRef.current.set(active.phase.index, distanceKm);
    const speech = buildWorkoutCompleteSpeech(elapsed, distanceKm);
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      void pushCoachGuideToWatch(buildWorkoutCompleteGuidePayload(elapsed, distanceKm));
    }
    if (audioEnabled && settings.coachGuide?.frequency !== 'watch_only') {
      announce(speech, { kind: 'coach', force: true, duckOthers: true });
      lastCoachAnnounceTimeRef.current = Date.now();
    }
  }, [
    coachModeActive,
    coachArmed,
    runActive,
    isPaused,
    audioEnabled,
    settings.coachGuide?.frequency,
    planStructure,
    elapsedSeconds,
    totalDistance,
    activePhaseIndex,
    announce,
    resolveLiveElapsedSeconds,
    resolveLiveDistanceMeters,
  ]);

  // Coach mode: rich intro that walks through today's plan, then arms live coach.
  // The intro IS the start announcement — no separate "Run started" cue.
  // watch_only doesn't need audio permission at all — it arms silently and never speaks.
  useEffect(() => {
    if (!coachModeActive || !runActive || isPaused) return;
    const isWatchOnlyNow = settingsRef.current.coachGuide?.frequency === 'watch_only';
    if (!audioEnabled && !isWatchOnlyNow) return;
    if (coachOpeningDoneRef.current) return;
    coachOpeningDoneRef.current = true;
    phaseStartDistanceKmByIndexRef.current.clear();
    phaseEndDistanceKmByIndexRef.current.clear();
    phaseStartElapsedSecondsByIndexRef.current.clear();
    phaseEndElapsedSecondsByIndexRef.current.clear();
    lastResolvedPhaseIndexRef.current = -1;
    activePhaseIndexRef.current = -1;
    maxDistanceIntoBlockByPhaseRef.current.clear();
    lastWatchGuideStructureKeyRef.current = '';
    lastWatchGuidePushAtRef.current = 0;
    console.log('[RunCoach] Opening sequence starting');

    const runCoachOpeningSequence = async () => {
      const voice = (settingsRef.current.audioVoice || 'female') as 'male' | 'female';
      const coach = settingsRef.current.coachGuide;
      if (!coach?.enabled || !coach.plan.trim()) return;

      const armCoach = () => {
        coachAudioReadyRef.current = true;
        // First live cue ~8s after arm (not a full cadence tick of silence).
        const tickMs = getCoachMinIntervalSeconds(
          settingsRef.current.coachGuide?.frequency ?? 'when_needed',
        ) * 1000;
        lastLiveCoachAtRef.current = Date.now() - tickMs + 8_000;
        setCoachArmed(true);
      };

      // Watch-only: arm immediately without waiting on audio permission or speaking
      // an intro — guidance reaches the runner via the watch guide payload instead.
      if (coach.frequency === 'watch_only') {
        hasAnnouncedStartRef.current = true;
        coachIntroAnnouncedRef.current = true;
        armCoach();
        console.log('[RunCoach] watch_only — silent arm, no intro speech');
        return;
      }
      if (!audioEnabled) return;

      // Wait briefly so profile (custom coach name) and HealthKit status are
      // loaded before composing the intro — avoids saying "Arnold" or
      // "Apple Health not connected" when the values are about to arrive.
      const waitStart = Date.now();
      while (Date.now() - waitStart < 1500) {
        const hasName = !!profileRef.current?.ai_coach_name?.trim();
        if (hasName) break;
        await new Promise((r) => setTimeout(r, 80));
      }

      // Get authoritative HealthKit state (native call is fast).
      let hkConnected = healthKitConnectedRef.current;
      if (user?.id) {
        try {
          hkConnected = await Promise.race([
            getHealthConnectedState(user.id),
            new Promise<boolean>((r) => setTimeout(() => r(hkConnected), 1200)),
          ]);
          setHealthKitConnected(hkConnected);
        } catch {
          // keep cached
        }
      }

      const title = profileRef.current?.ai_coach_name?.trim() || 'Arnold';
      const structure = planStructureRef.current;
      const planSummary = buildPlanIntroSpeech(coach.plan, structure);
      const activity = gpsKind === 'walk' ? 'walk' : gpsKind === 'hike' ? 'hike' : 'run';

      let planSentence: string;
      if (planSummary) {
        planSentence = ` Today's ${activity} is: ${planSummary}. I will guide you through every phase.`;
      } else if (coach.plan.trim().length > 8) {
        const shortPlan = coach.plan.trim().slice(0, 220).replace(/[.!?]+\s*$/, '');
        planSentence = ` Today's session: ${shortPlan}. I will be with you the whole way.`;
      } else {
        planSentence = ' I will guide you through this entire session.';
      }

      const hrNote = hkConnected
        ? ' I will also track your heart rate to make sure you stay in the right HR zone.'
        : ' Heart rate is not available yet, so I will coach on pace and structure.';

      const introText =
        `And we are off! Hi, I am ${title}, your run coach.` +
        ` I will guide you to reach your workout goals today.` +
        `${planSentence}${hrNote} Let us go!`;

      console.log('[RunCoach] Intro text:', introText);

      // Brief settle so audio session is ready after navigation.
      await new Promise((r) => setTimeout(r, 400));

      coachIntroInProgressRef.current = true;
      try {
        hasAnnouncedStartRef.current = true;
        stopCoachVoice();
        // Duck only when native playback starts (playDuckedAudio). Priming
        // duckOthers during the TTS fetch pauses Spotify with no speech yet.
        const introBlob = await prefetchCoachAudio(introText, voice);
        if (introBlob) {
          await playCoachAudio(introBlob);
        } else {
          await speakCoachCue(introText, { voice, useNatural: true, priority: true, interrupt: true });
        }
        coachIntroAnnouncedRef.current = true;
        Analytics.runningAudioAnnouncementPlayed('start');
        Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');
        lastCoachAnnounceTimeRef.current = Date.now();
        armCoach();
        console.log('[RunCoach] Intro complete, live coach is now armed');
      } finally {
        coachIntroInProgressRef.current = false;
        if (Capacitor.getPlatform() === 'ios') prepareBackgroundKeepAlive();
      }
    };

    void runCoachOpeningSequence().catch((err) => {
      console.error('[RunCoach] Opening sequence failed:', err);
      hasAnnouncedStartRef.current = true;
      coachIntroAnnouncedRef.current = true;
      coachAudioReadyRef.current = true;
      lastLiveCoachAtRef.current = Date.now();
      setCoachArmed(true);
      if (settingsRef.current.coachGuide?.frequency !== 'watch_only') {
        const voice = (settingsRef.current.audioVoice || 'female') as 'male' | 'female';
        void speakNativeNow('Coach is ready. Let us go.', voice);
      }
    });
  }, [coachModeActive, runActive, audioEnabled, isPaused, gpsKind, settings.coachGuide?.frequency]);

  // Normal run: fire "Run started" immediately on mount (native voice, no GPS wait).
  useEffect(() => {
    if (coachModeActive || !runActive || !audioEnabled) return;
    if (hasAnnouncedStartRef.current) return;
    if (settings.audioStats === 'off') return;
    hasAnnouncedStartRef.current = true;
    const voice = (settings.audioVoice || 'female') as 'male' | 'female';
    const startedText = runStartedSpeechRef.current() || 'Run started.';
    console.log('[Run] Speaking start cue immediately (native):', startedText);
    void speakNativeNow(startedText, voice);
    Analytics.runningAudioAnnouncementPlayed('start');
  }, [coachModeActive, runActive, audioEnabled, settings.audioStats, settings.audioVoice]);

  // Non-coach: clear any leftover queued cloud TTS from the countdown.
  useEffect(() => {
    if (!runActive || coachModeActive) return;
    stopCoachVoice();
  }, [runActive, coachModeActive]);

  const buildLiveCoachSnapshot = useCallback((): RunningCoachSnapshot | null => {
    const coach = settingsRef.current.coachGuide;
    if (!coach?.enabled || !coach.plan.trim()) return null;

    const elapsed = resolveLiveElapsedSeconds();
    const structure = planStructureRef.current;
    const distanceKm = resolveLiveDistanceMeters() / 1000;
    const averagePaceSecondsPerKm = distanceKm > 0.01 ? elapsed / distanceKm : 0;
    const lastSplit = splitTimesRef.current.at(-1);
    const previousSplit = splitTimesRef.current.at(-2);
    const lastSplitSeconds =
      lastSplit && lastSplit.elapsedSeconds <= elapsed + 10
        ? lastSplit.elapsedSeconds - (previousSplit?.elapsedSeconds ?? 0)
        : undefined;
    const currentPaceSecondsPerKm = resolveCurrentLivePace({
      watchLive: isWatchDistanceLive(),
      watchLivePace: watchLivePaceRef.current,
      watchRollingPace: watchRollingPaceRef.current,
      gpsWindow: gpsPaceWindowRef.current,
      lastGpsSpeedMps: lastGpsSpeedMpsRef.current,
      lastGpsSpeedAt: lastGpsSpeedAtRef.current,
    });
    const activeForCoach = getActivePhaseWithDistanceAnchors(
      structure,
      elapsed,
      distanceKm,
      phaseStartDistanceKmByIndexRef.current,
      phaseEndElapsedSecondsByIndexRef.current,
      lastResolvedPhaseIndexRef,
      phaseStartElapsedSecondsByIndexRef.current,
    );
    const phaseCoach = activeForCoach
      ? describeActivePhaseForCoach(activeForCoach, structure)
      : null;
    const phaseKind = activeForCoach?.phase.kind;
    const phaseTargetZone = activeForCoach?.phase.targetZone ?? null;
    const targetPaceSecondsPerKm = resolveLiveCoachTargetPace(
      phaseKind,
      activeForCoach?.phase.paceSecondsPerKm,
      coach.plan,
    );
    const racePacing = resolveRacePacingForSnapshot(
      coach.plan,
      phaseKind,
      activeForCoach?.phase.targetTimeSeconds,
      activeForCoach?.phase.distanceKm,
      activeForCoach?.phase.paceSecondsPerKm,
      distanceKm,
      elapsed,
      currentPaceSecondsPerKm,
    );
    const paceComparison =
      targetPaceSecondsPerKm && currentPaceSecondsPerKm > 0
        ? comparePaceToTarget(currentPaceSecondsPerKm, targetPaceSecondsPerKm)
        : null;
    const paceDeltaSeconds = paceComparison?.deltaSec ?? (
      targetPaceSecondsPerKm
        ? Math.round(currentPaceSecondsPerKm - targetPaceSecondsPerKm)
        : undefined
    );
    const bpm = currentBpmRef.current;

    return {
      plan: coach.plan,
      frequency: coach.frequency,
      distanceKm,
      totalDistanceMeters: Math.round(distanceKm * 1000),
      elapsedSeconds: elapsed,
      elapsedSpeech: formatElapsedSpeech(elapsed),
      currentPaceSecondsPerKm,
      averagePaceSecondsPerKm,
      lastSplitSeconds,
      paceDeltaSeconds,
      targetPaceSecondsPerKm,
      paceVerdict: paceComparison?.verdict,
      currentBpm: bpm ?? null,
      targetZone: phaseTargetZone,
      zones: hrZoneSettings.zones.map((z) => ({
        id: z.id,
        label: z.label,
        lowerBpm: z.lowerBpm,
        upperBpm: z.upperBpm,
      })),
      activePhaseLabel: phaseCoach?.label,
      activePhaseSummary: phaseCoach?.summary,
      nextPhaseLabel: phaseCoach?.nextPhaseLabel,
      phaseKind,
      remainingPhaseSeconds: (() => {
        const r = activeForCoach?.remainingSeconds;
        if (r == null || r <= 0 || r > 7200) return undefined;
        return r;
      })(),
      phaseElapsedSeconds: (() => {
        const dur = activeForCoach?.phase.durationSeconds;
        const rem = activeForCoach?.remainingSeconds;
        if (dur && rem != null && rem >= 0) return Math.max(0, dur - rem);
        return undefined;
      })(),
      remainingPhaseDistanceMeters:
        activeForCoach?.remainingDistanceKm != null
          ? Math.round(activeForCoach.remainingDistanceKm * 1000)
          : undefined,
      phaseDistanceIntoMeters:
        activeForCoach?.distanceIntoPhaseKm != null
          ? Math.round(activeForCoach.distanceIntoPhaseKm * 1000)
          : undefined,
      phaseStartDistanceMeters: activeForCoach?.phaseStartDistanceMeters,
      phaseCompleteAtDistanceMeters: activeForCoach?.phaseCompleteAtDistanceMeters,
      intervalRep: activeForCoach?.phase.repInSet?.rep,
      intervalTotalReps: activeForCoach?.phase.repInSet?.totalReps,
      isLastInterval:
        activeForCoach?.phase.repInSet != null &&
        activeForCoach.phase.repInSet.rep === activeForCoach.phase.repInSet.totalReps,
      raceLabel: racePacing?.raceLabel,
      targetDistanceKm: racePacing?.targetDistanceKm,
      targetTimeSeconds: racePacing?.targetTimeSeconds,
      projectedFinishSeconds: racePacing?.projectedFinishSeconds,
      scheduleDeltaSeconds: racePacing?.scheduleDeltaSeconds,
      requiredPaceSecondsPerKm: racePacing?.requiredPaceSecondsPerKm,
    };
  }, [resolveLiveElapsedSeconds, resolveLiveDistanceMeters, hrZoneSettings.zones, isWatchDistanceLive]);

  const buildLiveCoachFallback = useCallback((snapshot: RunningCoachSnapshot): string | null => {
    const coach = settingsRef.current.coachGuide;
    if (!coach) return null;
    return buildLocalRunningGuidance(snapshot);
  }, []);

  // Personalized Arnold coach — cadence matches frequency even when cloud TTS is slow
  // or iOS throttles timers with the screen locked (also tick from watch metrics).
  useEffect(() => {
    if (!coachModeActive || !settings.coachGuide?.enabled || !settings.coachGuide.plan.trim()) return;
    if (isPaused || !runActive || !coachArmed) return;

    const coach = settings.coachGuide;
    const isWatchOnly = coach.frequency === 'watch_only';
    if (isWatchOnly) return;
    if (!audioEnabled) return;
    const tickMs = getCoachMinIntervalSeconds(coach.frequency) * 1000;

    const maybeCoach = () => {
      if (!runActiveRef.current || isPausedRef.current || runEndedRef.current) return;
      if (coachPlanCompletedRef.current) return;
      if (coachIntroInProgressRef.current) return;
      if (!coachAudioReadyRef.current) return;

      const now = Date.now();
      if (now - lastLiveCoachAtRef.current < tickMs) return;

      // Don't let a long TTS playback suppress the next cadence ticks.
      const shouldCutPrevious = coachInFlightRef.current;
      if (shouldCutPrevious) {
        coachInFlightRef.current = false;
      }

      const snapshot = buildLiveCoachSnapshot();
      if (!snapshot || snapshot.elapsedSeconds < 20) return;

      // Resolve speech BEFORE burning the cadence slot — empty resolveText used to
      // skip announcements for a full 30s interval.
      const text = sanitizeCoachSpeechText(buildLiveCoachFallback(snapshot)?.trim() || '');
      if (!text) return;
      const normalized = text.toLowerCase().replace(/\s+/g, ' ');
      const lastSpoken = lastCoachSpokenRef.current;
      if (lastSpoken.text === normalized && now - lastSpoken.at < tickMs) return;

      coachInFlightRef.current = true;
      lastLiveCoachAtRef.current = now;
      lastCoachSpokenRef.current = { text: normalized, at: now };
      Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');

      const voice = (settingsRef.current.audioVoice || 'female') as 'male' | 'female';
      void speakCoachCue(text, {
        voice,
        useNatural: true,
        live: true,
        priority: false,
        // Only cut a still-playing previous LIVE cue — don't always interrupt phase starts.
        interrupt: shouldCutPrevious,
      }).finally(() => {
        coachInFlightRef.current = false;
      });

      // Release the in-flight lock before playback finishes so the next tick can fire.
      window.setTimeout(() => {
        coachInFlightRef.current = false;
      }, Math.min(8_000, Math.max(2_000, tickMs - 2_000)));
    };

    maybeLiveCoachRef.current = maybeCoach;
    maybeCoach();
    // Sub-second poll: iOS throttles long intervals hard when the screen is locked.
    const id = window.setInterval(maybeCoach, 1000);
    return () => {
      window.clearInterval(id);
      if (maybeLiveCoachRef.current === maybeCoach) maybeLiveCoachRef.current = null;
    };
  }, [
    coachModeActive,
    coachArmed,
    settings.coachGuide?.enabled,
    settings.coachGuide?.plan,
    settings.coachGuide?.frequency,
    audioEnabled,
    isPaused,
    runActive,
    buildLiveCoachSnapshot,
    buildLiveCoachFallback,
  ]);
  
  // Detect whether the user has connected Apple Health. Used both to decide
  // whether to poll HR samples and to surface the "HR not available" notice
  // on the coach guide setup screen.
  useEffect(() => {
    if (!user?.id) return;
    void getHealthConnectedState(user.id).then(setHealthKitConnected).catch(() => setHealthKitConnected(false));
  }, [user?.id]);

  // When a run starts, begin HR polling from run start (not an arbitrary 60s window).
  useEffect(() => {
    if (!runActive) return;
    lastHrFetchEndRef.current = Math.max(0, startTimeRef.current - 15_000);
  }, [runActive]);

  // Live heart-rate polling from Apple HealthKit (Straiv native query + capgo fallback).
  // Always attempt on iOS during an active run — don't rely only on the "connected" flag.
  useEffect(() => {
    if (!runActive || isPaused) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

    let cancelled = false;
    const mergeHrSamples = (incoming: HrSample[]) => {
      if (incoming.length === 0) return;
      const runStart = startTimeRef.current;
      const now = Date.now();
      const filtered = normalizeHrSamples(incoming).filter(
        (s) =>
          s.timestamp >= runStart - 5_000 &&
          s.timestamp <= now + 2_000,
      );
      if (filtered.length === 0) return;

      const byTs = new Map<number, number>();
      for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
      for (const s of filtered) byTs.set(s.timestamp, s.bpm);
      hrSamplesRef.current = [...byTs.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([timestamp, bpm]) => ({ timestamp, bpm }));
      const latest = hrSamplesRef.current.at(-1);
      if (latest && now - latest.timestamp <= LIVE_HR_MAX_AGE_MS) {
        setCurrentBpm(latest.bpm);
        if (!healthKitConnectedRef.current && hrSamplesRef.current.length >= 2) {
          setHealthKitConnected(true);
        }
      } else if (hrSamplesRef.current.length === 0) {
        setCurrentBpm(null);
      }
    };

    const sampleNow = async () => {
      const end = new Date();
      const start = new Date(Math.max(0, startTimeRef.current - 15_000));
      const now = Date.now();
      try {
        const [latestBeat, windowSamples, watchMetrics] = await Promise.all([
          readLatestHeartRateBpm(LIVE_HR_MAX_AGE_MS).catch(() => null),
          readHeartRateSamplesInRange(start, end, 8000),
          getWatchLiveMetrics().catch(() => null),
        ]);
        if (cancelled) return;
        if (watchMetrics?.currentHeartRate && watchMetrics.currentHeartRate > 0) {
          setCurrentBpm(Math.round(watchMetrics.currentHeartRate));
        }
        const batch: HrSample[] = [...windowSamples];
        if (latestBeat && now - latestBeat.timestamp <= LIVE_HR_MAX_AGE_MS) {
          batch.push(latestBeat);
        }
        mergeHrSamples(batch);
        lastHrFetchEndRef.current = end.getTime();
        if (batch.length > 0) {
          console.log('[HR] synced', hrSamplesRef.current.length, 'samples (window', windowSamples.length, ')');
        }
      } catch (err) {
        console.warn('[HR] sample read failed', err);
      }
    };

    void sampleNow();
    const id = window.setInterval(() => { void sampleNow(); }, 10_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [runActive, isPaused]);

  // Structured plan driver: advances through Arnold's warmup → intervals →
  // cooldown sequence and speaks the start/end cue for each phase. Polled on a
  // short timer so phase changes fire with the watch, not after React state lag.
  useEffect(() => {
    if (!runActive || isPaused || !audioEnabled) return;
    if (!coachModeActive) return;
    if (planStructure.phases.length === 0) return;
    if (!coachArmed) return;
    if (coachPlanCompletedRef.current) return;

    const tickPhaseDriver = () => {
      if (!runActiveRef.current || isPausedRef.current || runEndedRef.current) return;
      if (coachPlanCompletedRef.current) return;
      if (coachIntroInProgressRef.current) return;

      const distanceKm = resolveLiveDistanceMeters() / 1000;
      const liveElapsed = resolveLiveElapsedSeconds();
      const active = getActivePhaseWithDistanceAnchors(
        planStructureRef.current,
        liveElapsed,
        distanceKm,
        phaseStartDistanceKmByIndexRef.current,
        phaseEndElapsedSecondsByIndexRef.current,
        lastResolvedPhaseIndexRef,
        phaseStartElapsedSecondsByIndexRef.current,
      );
      if (!active) return;

      const coachFrequency = settingsRef.current.coachGuide?.frequency ?? 'frequent';
      const isWatchOnly = coachFrequency === 'watch_only';

      // Phase change → interrupt immediately (never wait for live-coach cadence).
      // Use lastResolvedPhaseIndexRef (sync) so the 500ms poll cannot double-fire
      // before React re-renders activePhaseIndex.
      const prevSpokenIdx = activePhaseIndexRef.current;
      if (active.phase.index !== prevSpokenIdx) {
        const prevIdx = prevSpokenIdx;
        const distKmNow = distanceKm;
        if (prevIdx >= 0) {
          phaseEndDistanceKmByIndexRef.current.set(prevIdx, distKmNow);
        }
        phaseStartDistanceKmByIndexRef.current.set(active.phase.index, distKmNow);
        activePhaseIndexRef.current = active.phase.index;
        setActivePhaseIndex(active.phase.index);
        phaseStartedAtRef.current = Date.now();
        gpsPaceWindowRef.current = [];
        const skipGenericStart =
          coachIntroAnnouncedRef.current && active.phase.index === 0;
        if (!isWatchOnly && !skipGenericStart) {
          void announce(active.phase.startCue, {
            kind: 'coach',
            force: true,
            duckOthers: true,
            priority: true,
            interrupt: true,
          });
          lastCoachAnnounceTimeRef.current = Date.now();
          Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');
        }
        return;
      }

      if (isWatchOnly) return;
      // Structural end/transition cues: short anti-spam only (not full cadence).
      const sinceLastStructuralMs = Date.now() - lastCoachAnnounceTimeRef.current;
      if (sinceLastStructuralMs < 2_500) return;

      if (
        active.remainingSeconds != null &&
        active.remainingSeconds <= 15 &&
        active.remainingSeconds > 0 &&
        active.phase.endCue &&
        !announcedEndCueForPhaseRef.current.has(active.phase.index)
      ) {
        announcedEndCueForPhaseRef.current.add(active.phase.index);
        void announce(active.phase.endCue, {
          kind: 'coach',
          force: true,
          duckOthers: true,
          priority: true,
          interrupt: true,
        });
        lastCoachAnnounceTimeRef.current = Date.now();
        Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');
        return;
      }

      const transitionCue = buildPhaseTransitionCue(active);
      const transitionKey = getPhaseTransitionCueKey(active);
      if (transitionCue && transitionKey) {
        if (!announcedTransitionKeysRef.current.has(transitionKey)) {
          announcedTransitionKeysRef.current.add(transitionKey);
          void announce(transitionCue, {
            kind: 'coach',
            force: true,
            duckOthers: true,
            priority: true,
            interrupt: true,
          });
          lastCoachAnnounceTimeRef.current = Date.now();
          Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');
        }
      }
    };

    tickPhaseDriver();
    const id = window.setInterval(tickPhaseDriver, 500);
    return () => window.clearInterval(id);
  }, [
    planStructure,
    audioEnabled,
    isPaused,
    runActive,
    coachModeActive,
    coachArmed,
    announce,
    resolveLiveElapsedSeconds,
    resolveLiveDistanceMeters,
  ]);

  // Stop any in-flight TTS when the user leaves the screen.
  useEffect(() => {
    return () => {
      stopCoachVoice();
    };
  }, []);

  // Standalone HR cues only when AI coach is off (coach guidance includes HR).
  useEffect(() => {
    if (coachModeActive) return;
    if (!currentBpm || !audioEnabled || isPaused || !runActive) return;
    if (!settings.coachGuide?.enabled) return;
    const now = Date.now();
    if (now - lastHrAnnounceTimeRef.current < 60_000) return;
    const cue = buildHrZoneVoiceCue(currentBpm, hrZoneSettings.zones, targetZone);
    if (!cue) return;
    // Only speak when out of target zone, or every ~3 minutes when on target.
    const onTarget = !targetZone || cue.zone === targetZone;
    if (onTarget && now - lastHrAnnounceTimeRef.current < 180_000) return;
    lastHrAnnounceTimeRef.current = now;
    announce(cue.message, { kind: 'stats', force: true, duckOthers: true });
    Analytics.runningAudioAnnouncementPlayed('coach_guidance');
      Analytics.liveCoachCuePlayed('guidance');
  }, [currentBpm, audioEnabled, isPaused, runActive, settings.coachGuide?.enabled, targetZone, hrZoneSettings.zones, announce, coachModeActive]);

  // Timer — monotonic + watch-synced so seconds never jump backward (47→46→48).
  useEffect(() => {
    if (runActive && !isPaused) {
      timerRef.current = setInterval(() => {
        const nowMs = Date.now();
        dropStaleWatchAuthority();
        gpsPaceWindowRef.current = ageGpsPaceWindow(gpsPaceWindowRef.current, nowMs);
        const phone = Math.floor(
          (nowMs - startTimeRef.current - pausedTimeRef.current) / 1000,
        );
        const watchLive = resolveLiveElapsedSeconds();
        const followWatch =
          watchClockReadyRef.current &&
          (isWatchDistanceLive() || watchStreamingRef.current);
        const next = followWatch
          ? Math.max(phone, watchLive)
          : Math.max(phone, 0);
        setElapsedSeconds(next);
        elapsedSecondsRef.current = next;
      }, 250);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [runActive, isPaused, resolveLiveElapsedSeconds, dropStaleWatchAuthority, isWatchDistanceLive]);

  // GPS tracking - BackgroundGeolocation on native (works when screen locked), fallback to standard
  useEffect(() => {
    if (!runActive) return;
    const isNative = Capacitor.isNativePlatform();
    let isMounted = true;
    
    const processLocation = (
      lat: number,
      lng: number,
      accuracy: number,
      timestamp: number,
      speedMps?: number,
    ) => {
      if (!isMounted || runEndedRef.current) return;
      lastGpsUpdateRef.current = Date.now();
      if (speedMps != null && Number.isFinite(speedMps) && speedMps >= 0) {
        lastGpsSpeedMpsRef.current = speedMps;
        lastGpsSpeedAtRef.current = Date.now();
      }
      const newPoint: GpsPoint = {
        lat,
        lng,
        timestamp: timestamp || Date.now(),
        accuracy,
        speedMps: speedMps != null && speedMps >= 0 ? speedMps : undefined,
      };
      
      if (newPoint.accuracy) {
        if (newPoint.accuracy <= 5) setGpsSignal('strong');
        else if (newPoint.accuracy <= 15) setGpsSignal('medium');
        else if (newPoint.accuracy <= 30) setGpsSignal('weak');
        else setGpsSignal('searching');
      }
      
      if (newPoint.accuracy && newPoint.accuracy > 30) return;
      
      setCurrentPosition([newPoint.lat, newPoint.lng]);
      
      if (lastPositionRef.current) {
        const dist = calculateDistance(
          lastPositionRef.current.lat,
          lastPositionRef.current.lng,
          newPoint.lat,
          newPoint.lng
        );
        // Do not invent new segments on gaps — keep real GPS points flowing via native buffer.
        if (dist > 3) {
          const nowMs = Date.now();
          dropStaleWatchAuthority();
          phoneGpsDistanceRef.current += dist;
          const watchLive = isWatchDistanceLive();
          if (watchLive) {
            void pushPhoneDistanceToWatch(phoneGpsDistanceRef.current);
            const paceWindow = gpsPaceWindowRef.current;
            paceWindow.push({ t: nowMs, dist: phoneGpsDistanceRef.current });
            gpsPaceWindowRef.current = paceWindow.filter((p) => p.t >= nowMs - 90_000);
          } else {
            const newTotal = totalDistanceRef.current + dist;
            totalDistanceRef.current = newTotal;
            const paceWindow = gpsPaceWindowRef.current;
            paceWindow.push({ t: nowMs, dist: newTotal });
            gpsPaceWindowRef.current = paceWindow.filter((p) => p.t >= nowMs - 90_000);
            const splitMeters = splitLengthMeters(unitsRef.current);
            const currentSplit = Math.floor(newTotal / splitMeters);
            if (currentSplit > lastSplitKmRef.current && currentSplit > 0) {
              const currentElapsed = Math.floor((nowMs - startTimeRef.current - pausedTimeRef.current) / 1000);
              lastSplitKmRef.current = currentSplit;
              splitTimesRef.current.push({ km: currentSplit, elapsedSeconds: currentElapsed });
            }
            setTotalDistance(newTotal);
          }
          setRouteSegments(prev => {
            const last = prev[prev.length - 1];
            return [...prev.slice(0, -1), [...last, newPoint]];
          });
          lastPositionRef.current = newPoint;
        } else if (dist <= 3) {
          // Still update last point timestamp/speed so gaps don't look larger than they are.
          lastPositionRef.current = { ...lastPositionRef.current, timestamp: newPoint.timestamp, speedMps: newPoint.speedMps };
        }
      } else {
        setRouteSegments(prev => {
          const last = prev[prev.length - 1];
          if (last.length === 0) return [[newPoint]];
          return [...prev.slice(0, -1), [...last, newPoint]];
        });
        lastPositionRef.current = newPoint;
      }
    };
    
    const startTracking = async () => {
      try {
        const permission = locationPermissionConfirmedRef.current
          ? { location: 'granted' }
          : await Geolocation.checkPermissions();
        if (!isMounted) return;
        if (permission.location !== 'granted') {
          // RunningStart owns the user-facing permission prompt. If this fails,
          // permission was revoked after the pre-run gate.
          return;
        }
        locationPermissionConfirmedRef.current = true;
        
        const quickPosition = await Geolocation.getCurrentPosition({
          enableHighAccuracy: false,
          timeout: 3000,
          maximumAge: 30000,
        }).catch(() => null);
        
        if (isMounted && quickPosition) {
          const quickPoint: GpsPoint = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy,
          };
          setCurrentPosition([quickPoint.lat, quickPoint.lng]);
          setGpsSignal('weak');
        }
        
        let initialPosition: Position | null = null;
        try {
          initialPosition = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000,
          });
        } catch (posErr) {
          if (quickPosition && isMounted) {
            const fallback: GpsPoint = {
              lat: quickPosition.coords.latitude,
              lng: quickPosition.coords.longitude,
              timestamp: quickPosition.timestamp,
              accuracy: quickPosition.coords.accuracy,
            };
            const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
            const isResuming = existingSegments.flat().length > 0;
            if (!isResuming) {
              setRouteSegments(prev => (prev.length === 0 || (prev[prev.length - 1]?.length ?? 0) === 0 ? [[fallback]] : [...prev.slice(0, -1), [...(prev[prev.length - 1] || []), fallback]]));
              lastPositionRef.current = fallback;
            } else {
              lastPositionRef.current = lastPositionRef.current ?? existingSegments.flat().at(-1) ?? fallback;
            }
          }
        }
        
        if (!isMounted) return;
        
        // Android 13+: Request notification permission so foreground service notification can show
        // (Required for GPS/splits to work when screen is off - Doze won't suspend us)
        if (isNative && Capacitor.getPlatform() === 'android' && typeof Notification !== 'undefined' && Notification.permission === 'default') {
          try {
            await Notification.requestPermission();
          } catch (_) {}
        }
        
        // When resuming: set lastPositionRef to CURRENT position so we don't add "walk during pause" to route
        const existingSegments = sessionDataRef.current?.routeSegments ?? (sessionDataRef.current?.routePoints ? [sessionDataRef.current.routePoints] : [[]]);
        const allPoints = existingSegments.flat();
        const isResuming = allPoints.length > 0;
        
        if (initialPosition) {
          const initialPoint: GpsPoint = {
            lat: initialPosition.coords.latitude,
            lng: initialPosition.coords.longitude,
            timestamp: initialPosition.timestamp,
            accuracy: initialPosition.coords.accuracy,
          };
          setCurrentPosition([initialPoint.lat, initialPoint.lng]);
          if (isResuming) {
            // Keep the previous route point as the distance anchor if we still
            // have it. BackgroundGeolocation should deliver intermediate points;
            // resetting here can hide watcher gaps and create confusing routes.
            lastPositionRef.current = lastPositionRef.current ?? allPoints[allPoints.length - 1] ?? initialPoint;
          } else {
            setRouteSegments(prev => {
              const last = prev[prev.length - 1];
              if (last.length === 0) return [[initialPoint]];
              return [...prev.slice(0, -1), [...last, initialPoint]];
            });
            lastPositionRef.current = initialPoint;
          }
        } else if (isResuming && quickPosition && isMounted) {
          lastPositionRef.current = {
            lat: quickPosition.coords.latitude,
            lng: quickPosition.coords.longitude,
            timestamp: quickPosition.timestamp,
            accuracy: quickPosition.coords.accuracy ?? 50,
          };
        } else if (isResuming && allPoints.length > 0) {
          lastPositionRef.current = allPoints[allPoints.length - 1];
        }
        
        // "Run started" is announced by the mount effects (immediate native for
        // normal runs; rich intro for coach runs) — not after GPS lock.
        
        if (isNative && !isPaused) {
          // Optional native buffer: only used to fill gaps if JS/BG freezes while locked.
          // BackgroundGeolocation remains the primary route source (same path as before).
          void startNativeRunGpsTracking().catch((e) =>
            console.warn('[GPS] Native gap-buffer failed to start:', e),
          );
          try {
            const bgGeo = getBackgroundGeolocation();
            if (bgGeo) {
              const watcherId = await bgGeo.addWatcher(
                {
                  backgroundMessage: t('running.active.notification.body'),
                  backgroundTitle: t('running.active.notification.title'),
                  requestPermissions: false,
                  distanceFilter: 3,
                  stale: false,
                },
                (location: {
                  latitude: number;
                  longitude: number;
                  accuracy?: number;
                  time?: number;
                  speed?: number;
                } | null, error: any) => {
                  if (error) {
                    return console.warn('[GPS] Background error:', error);
                  }
                  if (location && isMounted) {
                    processLocation(
                      location.latitude,
                      location.longitude,
                      location.accuracy ?? 50,
                      location.time ?? Date.now(),
                      location.speed,
                    );
                  }
                }
              );
              if (isMounted) {
                bgWatcherIdRef.current = watcherId;
                console.log('[GPS] Background watcher started');
              }
            } else {
              throw new Error('Plugin not available');
            }
          } catch (bgErr: any) {
            const errMsg = bgErr?.message ?? String(bgErr);
            const errCode = bgErr?.code ?? bgErr?.name;
            console.warn('[GPS] Background geolocation unavailable, using standard:', { message: errMsg, code: errCode, err: bgErr });
            const watchId = await Geolocation.watchPosition(
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
              (position: Position | null, err) => {
                if (err || !position || !isMounted) return;
                processLocation(
                  position.coords.latitude,
                  position.coords.longitude,
                  position.coords.accuracy ?? 50,
                  position.timestamp,
                  position.coords.speed ?? undefined,
                );
              }
            );
            if (isMounted) watchIdRef.current = watchId;
          }
        } else {
          const watchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 1000 },
            (position: Position | null, err) => {
              if (err || !position || !isMounted) return;
              if (isPaused) return;
              processLocation(
                position.coords.latitude,
                position.coords.longitude,
                position.coords.accuracy ?? 50,
                position.timestamp,
                position.coords.speed ?? undefined,
              );
            }
          );
          if (isMounted) watchIdRef.current = watchId;
        }
      } catch (error: any) {
        console.error('[GPS] Failed to start tracking:', error);
        if (isMounted) toast.error(t('running.active.toast.gpsError', { message: error.message || 'Unknown error' }));
      }
    };
    
    if (runActive && !isPaused) {
      startTracking();
    }
    
    const staleWatchdog = window.setInterval(() => {
      if (!isMounted || isPaused) return;
      if (Date.now() - lastGpsUpdateRef.current > 45_000) {
        console.warn('[GPS] Stale location data — restarting GPS watchers');
        lastGpsUpdateRef.current = Date.now();
        setGpsRestartKey((k) => k + 1);
      }
    }, 15_000);

    // Drain native buffer only when BG/JS looks stale — fills real gap points, does not replace primary GPS.
    const nativeDrain = window.setInterval(() => {
      if (!isMounted || isPaused || !isNative) return;
      const staleMs = Date.now() - lastGpsUpdateRef.current;
      if (staleMs < 8_000 && lastGpsUpdateRef.current > 0) {
        // Primary path is healthy — discard native duplicates so we don't double-count.
        void drainNativeRunGpsPoints().catch(() => {});
        return;
      }
      void drainNativeRunGpsPoints()
        .then((points) => {
          if (!isMounted || !points.length) return;
          for (const p of points) {
            processLocation(p.lat, p.lng, p.accuracy ?? 50, p.timestamp, p.speed);
          }
        })
        .catch(() => {});
    }, 2000);

    return () => {
      isMounted = false;
      window.clearInterval(staleWatchdog);
      window.clearInterval(nativeDrain);
      if (bgWatcherIdRef.current) {
        getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
        bgWatcherIdRef.current = null;
      }
      if (watchIdRef.current) {
        Geolocation.clearWatch({ id: watchIdRef.current }).catch(console.warn);
        watchIdRef.current = null;
      }
      // Only stop native tracking when the run is no longer active (not on pause/restart).
      if (!runActiveRef.current || runEndedRef.current) {
        void stopNativeRunGpsTracking().catch(() => {});
      }
    };
  }, [runActive, isPaused, announce, navigate, gpsRestartKey, t, gpsKind, dropStaleWatchAuthority, isWatchDistanceLive]);
  
  // Track pause duration for analytics
  const pauseStartTimeRef = useRef<number>(0);
  
  // Handle pause - start new segment so movement during pause is not added to route.
  // `broadcast` is false when the pause originated on the Apple Watch (avoids a sync loop).
  const handlePause = (broadcast = true) => {
    if (isLocked) return;
    if (isPausedRef.current) return; // already paused (e.g. duplicate watch event)

    if (broadcast && Capacitor.getPlatform() === 'ios') void pauseWatchRun();

    const newPauseCount = pauseCount + 1;
    setIsPaused(true);
    setPauseCount(newPauseCount);
    pausedTimeRef.current = Date.now() - startTimeRef.current - (elapsedSeconds * 1000);
    pauseStartTimeRef.current = Date.now(); // Track when pause started for analytics
    // Stop any in-flight coach audio so "Paused" lands instantly, then speak
    // via on-device voice (no cloud round-trip = no delay).
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionPaused'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Start new segment - movement during pause will go here but we clear it on resume
    setRouteSegments(prev => [...prev, []]);
    
    // Log pause event
    Analytics.runningWorkoutPaused(elapsedSeconds, totalDistance, newPauseCount);
    Analytics.runningAudioAnnouncementPlayed('pause');
    
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    if (bgWatcherIdRef.current) {
      getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
      bgWatcherIdRef.current = null;
    }
    // Stop native buffer so pause movement is not merged into the route on resume.
    void stopNativeRunGpsTracking().catch(() => {});
  };
  
  // Handle resume - empty segment from pause will receive new run points (no line from pause→resume).
  // `broadcast` is false when the resume originated on the Apple Watch (avoids a sync loop).
  const handleResume = (broadcast = true) => {
    if (isLocked || !isRunning || runEndedRef.current) return;
    if (!isPausedRef.current) return; // already running (e.g. duplicate watch event)

    if (broadcast && Capacitor.getPlatform() === 'ios') void resumeWatchRun();

    const pauseDuration = pauseStartTimeRef.current > 0 
      ? Math.round((Date.now() - pauseStartTimeRef.current) / 1000)
      : 0;
    
    setIsPaused(false);
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionResumed'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Log resume event
    Analytics.runningWorkoutResumed(elapsedSeconds, totalDistance, pauseDuration);
    Analytics.runningAudioAnnouncementPlayed('resume');
  };

  // Keep refs to the latest pause/resume handlers so the (once-registered) watch
  // listener can drive them without re-broadcasting back to the watch.
  const handlePauseRef = useRef(handlePause);
  handlePauseRef.current = handlePause;
  const handleResumeRef = useRef(handleResume);
  handleResumeRef.current = handleResume;
  
  // Track when stop dialog opened for paused time calculation
  const stopDialogOpenedRef = useRef<number>(0);
  
  const avgHeartRateForSegmentWindow = useCallback(() => {
    const startMs = startTimeRef.current + segmentStartElapsedRef.current * 1000;
    const samples = hrSamplesRef.current.filter((s) => s.timestamp >= startMs - 1000);
    if (samples.length === 0) return 0;
    return Math.round(samples.reduce((sum, s) => sum + s.bpm, 0) / samples.length);
  }, []);

  const finalizeTrailingSegment = useCallback(() => {
    // Only auto-close a trailing lap when the athlete already marked at least one segment.
    if (runSegmentsRef.current.length === 0) return;
    const elapsed = elapsedSecondsRef.current;
    const dist = totalDistanceRef.current;
    const duration = Math.max(0, elapsed - segmentStartElapsedRef.current);
    const distance = Math.max(0, dist - segmentStartDistanceRef.current);
    if (duration <= 5 && distance <= 10) return;
    const index = runSegmentsRef.current.length + 1;
    const avgHeartRateBpm = avgHeartRateForSegmentWindow();
    runSegmentsRef.current.push({ index, durationSeconds: duration, distanceMeters: distance, avgHeartRateBpm });
    setRunSegments(
      runSegmentsRef.current.map((seg) => ({
        ...seg,
        time: formatTime(Math.round(seg.durationSeconds)),
      })),
    );
  }, [avgHeartRateForSegmentWindow]);

  // Handle stop button - show confirmation and pause timer
  // Mark a training segment (syncs to Apple Watch).
  const handleMarkSegment = () => {
    if (isLocked || !isRunning || runEndedRef.current) return;
    const now = Date.now();
    // WKWebView often delivers the same tap as touch + click + delayed click (~0.4–1.5s).
    if (now < markSegmentLockUntilRef.current) return;
    markSegmentLockUntilRef.current = now + 2000;
    const elapsed = elapsedSecondsRef.current;
    const dist = totalDistanceRef.current;
    const duration = Math.max(0, elapsed - segmentStartElapsedRef.current);
    const distance = Math.max(0, dist - segmentStartDistanceRef.current);
    const index = runSegmentsRef.current.length + 1;
    const avgHeartRateBpm = avgHeartRateForSegmentWindow();
    runSegmentsRef.current.push({ index, durationSeconds: duration, distanceMeters: distance, avgHeartRateBpm });
    setRunSegments(
      runSegmentsRef.current.map((seg) => ({
        ...seg,
        time: formatTime(Math.round(seg.durationSeconds)),
      })),
    );
    segmentStartElapsedRef.current = elapsed;
    segmentStartDistanceRef.current = dist;
    const paceSecondsPerKm = distance > 10 ? duration / (distance / 1000) : 0;
    setSegmentPopup({ index, durationSeconds: duration, distanceMeters: distance, paceSecondsPerKm });
    window.setTimeout(() => setSegmentPopup(null), 3000);
    if (Capacitor.getPlatform() === 'ios') void markSegmentOnWatch();
  };

  const handleStopClick = () => {
    if (isLocked) return;
    // Save the time when dialog opened
    stopDialogOpenedRef.current = Date.now();
    // Pause the timer while showing confirmation
    setIsPaused(true);
    setShowStopConfirm(true);
    
    // Log stop dialog opened
    Analytics.runningStopDialogOpened();
  };
  
  // Cancel stop - resume the run and account for paused time
  const handleCancelStop = () => {
    if (runEndedRef.current || stopConfirmedRef.current) return;
    
    setShowStopConfirm(false);
    // Add the time spent in dialog to paused time
    if (stopDialogOpenedRef.current > 0) {
      const dialogTime = Date.now() - stopDialogOpenedRef.current;
      pausedTimeRef.current += dialogTime;
      stopDialogOpenedRef.current = 0;
    }
    setIsPaused(false);
    void stopCoachVoice();
    void speakNativeNow(t('running.gpsActivity.sessionResumed'), (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    
    // Log stop dialog cancelled
    Analytics.runningStopDialogCancelled();
  };
  
  // Confirm stop and finish workout
  const autoSaveRunWorkout = useCallback(async (summary: Record<string, unknown> | null | undefined) => {
    if (!summary || savedWorkoutIdRef.current || runDiscardedRef.current) return;
    const runSaveSessionKey = `straiv_run_saved_${startTimeRef.current}`;
    const runExerciseSessionKey = `straiv_run_exercise_${startTimeRef.current}`;
    try {
      const existingId = sessionStorage.getItem(runSaveSessionKey);
      if (existingId) {
        savedWorkoutIdRef.current = existingId;
        savedExerciseIdRef.current = sessionStorage.getItem(runExerciseSessionKey);
        return;
      }
    } catch {
      /* sessionStorage unavailable */
    }

    if (autoSaveInFlightRef.current) {
      await autoSaveInFlightRef.current.catch(() => undefined);
      if (savedWorkoutIdRef.current) return;
    }

    const saveTask = (async () => {
      const distanceKm = Number(summary.distance) || totalDistanceRef.current / 1000;
      const durationSec = Math.max(
        1,
        Number(summary.durationSeconds) ||
          Math.round((Number(summary.duration) || 0) * 60) ||
          elapsedSecondsRef.current,
      );
      const enrichedRunSegments = enrichSegmentsWithAvgHr(
        runSegmentsRef.current,
        startTimeRef.current,
        hrSamplesRef.current,
      ).map((seg) => ({
        ...seg,
        time: formatTime(Math.round(seg.durationSeconds)),
      }));

      try {
        const workoutTitle = defaultTitleWithKm(distanceKm);
        const { data: workout, error: workoutError } = await withPluginTimeout(
          createWorkout({
            title: workoutTitle,
            description: workoutDescriptionLine(distanceKm, formatTime(durationSec)),
            workout_type: gpsWorkoutType,
            duration_minutes: Math.round(durationSec / 60),
            calories_burned: Number(summary.estimatedCalories) || 0,
            difficulty_level: 'Moderate',
            notes: '',
            started_at: new Date(startTimeRef.current).toISOString(),
            completed_at: new Date().toISOString(),
          }),
          25000,
          { data: null, error: new Error('timeout') },
        );

        if (workoutError || !workout?.id) {
          console.error('[Run] Auto-save createWorkout failed:', workoutError);
          toast.error(t('running.summary.toast.saveFailedConnection'));
          return;
        }

        if (runDiscardedRef.current) {
          await deleteWorkout(workout.id).catch(() => undefined);
          return;
        }

        // Always keep the workout id even if details timeout — otherwise Save has nothing to update.
        savedWorkoutIdRef.current = workout.id;
        try {
          sessionStorage.setItem(runSaveSessionKey, workout.id);
        } catch {
          /* sessionStorage unavailable */
        }

        const { data: exercise, error: exerciseError } = await withPluginTimeout(
          addExerciseToWorkout(workout.id, {
            name: gpsExerciseName,
            muscle_groups: ['legs', 'cardiovascular'],
            category: 'Cardio',
            duration_seconds: durationSec,
            distance_km: distanceKm,
            notes: buildGpsRunExerciseNotesJson({
              workoutSummary: summary,
              kmSplits: (summary.kmSplits as { km: number; time: string; pace: string; isPartial?: boolean }[]) ?? [],
              enrichedRunSegments,
              routePoints,
              watchZoneSeconds: watchZoneSecondsRef.current,
              pauseCount,
              feelingEmoji: '',
              autoSaved: true,
              slim: true,
            }),
          }, { skipRefresh: true }),
          25000,
          { data: null, error: new Error('timeout') },
        );

        if (exerciseError) {
          console.error('[Run] Auto-save exercise failed (workout kept):', exerciseError);
          toast.error(t('running.summary.toast.saveDetailsFailed'));
          return;
        }

        if (runDiscardedRef.current) {
          await deleteWorkout(workout.id).catch(() => undefined);
          savedWorkoutIdRef.current = null;
          return;
        }

        savedExerciseIdRef.current = exercise?.id ?? null;
        try {
          if (exercise?.id) {
            sessionStorage.setItem(runExerciseSessionKey, exercise.id);
          }
        } catch {
          /* sessionStorage unavailable */
        }
        console.log('[Run] Auto-saved workout on stop:', workout.id);
      } catch (err) {
        console.error('[Run] Auto-save failed:', err);
        toast.error(t('running.summary.toast.saveGenericError'));
      }
    })();

    autoSaveInFlightRef.current = saveTask;
    try {
      await saveTask;
    } finally {
      autoSaveInFlightRef.current = null;
    }
  }, [
    addExerciseToWorkout,
    createWorkout,
    deleteWorkout,
    gpsExerciseName,
    gpsWorkoutType,
    pauseCount,
    routePoints.length,
    t,
    workoutDescriptionLine,
  ]);

  const autoSaveRunWorkoutRef = useRef(autoSaveRunWorkout);
  autoSaveRunWorkoutRef.current = autoSaveRunWorkout;

  const handleConfirmStop = async () => {
    if (runEndedRef.current) return;
    runEndedRef.current = true;
    stopConfirmedRef.current = true;
    runActiveRef.current = false;

    let stopElapsed = elapsedSecondsRef.current;
    let stopDistanceM = totalDistanceRef.current;

    // Freeze metrics immediately — don't accumulate distance/time during async stop work.
    if (bgWatcherIdRef.current) {
      getBackgroundGeolocation()?.removeWatcher({ id: bgWatcherIdRef.current }).catch(console.warn);
      bgWatcherIdRef.current = null;
    }
    if (watchIdRef.current) {
      Geolocation.clearWatch({ id: watchIdRef.current });
      watchIdRef.current = null;
    }
    void stopNativeRunGpsTracking().catch(() => {});
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setElapsedSeconds(stopElapsed);
    setTotalDistance(stopDistanceM);

    Analytics.runningStopDialogConfirmed();
    Analytics.runningWorkoutStopped(stopElapsed, stopDistanceM, true);

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      // End the watch FIRST so coachGuide complete cannot steal application context.
      if (!watchEndedStopRef.current) {
        await withPluginTimeout(signalWatchRunEnd(), 2500, undefined);
      }
      const distKm = stopDistanceM / 1000;
      if (coachModeActive) {
        void pushCoachGuideToWatch(buildWorkoutCompleteGuidePayload(stopElapsed, distKm));
        if (audioEnabled && !coachPlanCompletedRef.current) {
          void announce(buildWorkoutCompleteSpeech(stopElapsed, distKm), {
            kind: 'coach',
            force: true,
            duckOthers: true,
          });
        }
      } else if (watchEndedStopRef.current) {
        void pushCoachGuideToWatch({ active: false });
      }
    }

    void RunLiveActivity.end();
    void stopCoachVoice();

    setShowStopConfirm(false);
    setIsStoppingRun(true);
    setIsRunning(false);
    setIsPaused(true);

    const buildFallbackSummary = () => {
      const fallbackKm = totalDistanceRef.current / 1000;
      const fallbackElapsed = elapsedSecondsRef.current;
      const fallbackPace =
        fallbackElapsed > 0 && fallbackKm > 0 ? fallbackElapsed / fallbackKm : 0;
      return {
        type: 'running',
        title: defaultTitleWithKm(fallbackKm),
        duration: Math.round(fallbackElapsed / 60),
        durationSeconds: fallbackElapsed,
        distance: parseFloat(fallbackKm.toFixed(2)),
        pace: fallbackPace > 0 ? formatPace(fallbackPace) : '--:--',
        notes: '',
        estimatedCalories: 0,
        pauseCount,
        gpsPointsRecorded: routePoints.length,
        kmSplits: [] as { km: number; time: string; pace: string; isPartial?: boolean }[],
        _ts: Date.now(),
        _fallback: true,
      };
    };

    let stopSafeguard: number | undefined = window.setTimeout(() => {
      console.warn('[Run] Stop safeguard — forcing summary with phone metrics');
      if (!workoutSummaryRef.current) {
        const fallback = buildFallbackSummary();
        workoutSummaryRef.current = fallback;
        setWorkoutSummary(fallback);
      }
      setIsStoppingRun(false);
      setShowRunSummary(true);
      // Do not auto-save here — a thin save would block the rich summary from persisting.
    }, 25000);

    try {
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && startTimeRef.current > 0) {
        try {
          const start = new Date(startTimeRef.current - 10_000);
          const end = new Date();
          const backfill = await readHeartRateSamplesInRange(start, end, 5_000);
          if (backfill.length > 0) {
            const runEnd = end.getTime();
            const filtered = backfill.filter(
              (s) =>
                s.timestamp >= startTimeRef.current - 5_000 &&
                s.timestamp <= runEnd + 5_000,
            );
            const byTs = new Map<number, number>();
            for (const s of hrSamplesRef.current) byTs.set(s.timestamp, s.bpm);
            for (const s of filtered) byTs.set(s.timestamp, s.bpm);
            hrSamplesRef.current = [...byTs.entries()]
              .sort((a, b) => a[0] - b[0])
              .map(([timestamp, bpm]) => ({ timestamp, bpm }));
          }
        } catch (err) {
          console.warn('[HR] end-of-run backfill failed', err);
        }

        const watchEndTimeout = watchEndedStopRef.current ? 6000 : 4000;
        watchEndedStopRef.current = false;
        let watchWorkout = pendingWatchWorkoutRef.current
          ?? await withPluginTimeout(
            fetchLastCompletedWatchWorkoutForRun(startTimeRef.current, watchEndTimeout),
            watchEndTimeout + 1500,
            null,
          );
        pendingWatchWorkoutRef.current = null;
        if (watchWorkout) {
          ({ stopElapsed, stopDistanceM } = mergeWatchCompletedWorkout(watchWorkout, stopElapsed, stopDistanceM));
          await withPluginTimeout(clearLastCompletedWatchWorkout(), 2000, undefined);
        } else {
          const live = await withPluginTimeout(getWatchLiveMetrics(), 3000, null);
          if (live) {
            mergeWatchLiveMetrics(live);
            if (live.segments?.length) {
              runSegmentsRef.current = mergeWatchSegmentsIntoPhone(
                runSegmentsRef.current,
                live.segments.map((seg) => ({
                  index: Number(seg.index),
                  durationSeconds: seg.durationSeconds,
                  distanceMeters: seg.distanceMeters,
                  avgHeartRateBpm: seg.avgHeartRateBpm,
                })),
              );
            }
            if (
              live.distanceMeters != null &&
              live.distanceMeters > 0 &&
              watchIsAuthoritativeRef.current
            ) {
              stopDistanceM = live.distanceMeters;
            }
            if (live.elapsedSeconds != null && live.elapsedSeconds > stopElapsed) {
              stopElapsed = Math.round(live.elapsedSeconds);
              elapsedSecondsRef.current = stopElapsed;
              setElapsedSeconds(stopElapsed);
            }
          }
        }

        // Re-signal end last so earlier coachGuide updates cannot leave the watch running.
        await withPluginTimeout(signalWatchRunEnd(), 2500, undefined);
        await withPluginTimeout(endPhoneWorkoutSession(), 3000, undefined);
        setStraivNowPlaying(false);
      } else {
        await withPluginTimeout(endPhoneWorkoutSession(), 3000, undefined);
        setStraivNowPlaying(false);
      }

      const distanceKm = stopDistanceM / 1000;
    elapsedSecondsRef.current = stopElapsed;
    totalDistanceRef.current = stopDistanceM;
    setElapsedSeconds(stopElapsed);
    setTotalDistance(stopDistanceM);
    const paceSeconds = stopElapsed > 0 && distanceKm > 0 
      ? stopElapsed / distanceKm 
      : 0;
    const durationMinutes = Math.round(stopElapsed / 60);
    
    // Calculate calories using MET formula based on user weight
    // Running MET ≈ 9.8 (moderate pace), adjusted by speed
    // Formula: Calories = MET × weight(kg) × time(hours)
    const userWeightKg = profile?.weight_kg || 70; // Default 70kg if not set
    const avgSpeedKmh = stopElapsed > 0 
      ? distanceKm / (stopElapsed / 3600)
      : 0;
    
    // MET: running pace bands; walking/hiking use lower bases (ACSM-style approximations)
    let met = 8.0;
    if (gpsKind === 'walk') {
      if (avgSpeedKmh >= 6.5) met = 5.0;
      else if (avgSpeedKmh >= 5) met = 3.8;
      else met = 3.3;
    } else if (gpsKind === 'hike') {
      if (avgSpeedKmh >= 5.5) met = 9.0;
      else if (avgSpeedKmh >= 4) met = 7.0;
      else met = 6.0;
    } else {
      if (avgSpeedKmh >= 14) met = 13.5;
      else if (avgSpeedKmh >= 12) met = 11.5;
      else if (avgSpeedKmh >= 10) met = 9.8;
      else if (avgSpeedKmh >= 8) met = 8.3;
      else if (avgSpeedKmh >= 6) met = 6.0;
    }
    
    const timeHours = stopElapsed / 3600;
    const estimatedCalories = Math.round(met * userWeightKg * timeHours);
    
    const window = segmentWindowFromRecorded(runSegmentsRef.current);
    segmentStartElapsedRef.current = window.elapsedSeconds;
    segmentStartDistanceRef.current = window.distanceMeters;
    if (!segmentsCoverFullRun(runSegmentsRef.current, stopDistanceM, stopElapsed)) {
      finalizeTrailingSegment();
    }

    runSegmentsRef.current = enrichSegmentsWithAvgHr(
      runSegmentsRef.current,
      startTimeRef.current,
      hrSamplesRef.current,
    );
    setRunSegments(
      runSegmentsRef.current.map((seg) => ({
        ...seg,
        time: formatTime(Math.round(seg.durationSeconds)),
      })),
    );

    // Create comprehensive workout summary for Arnold (matching Chat.tsx expected format)
    const runEnd = Date.now();
    const hrForReport = filterHrSamplesToRunWindow(hrSamplesRef.current, runEnd);
    const activeIdx = lastResolvedPhaseIndexRef.current;
    if (activeIdx >= 0 && !phaseEndDistanceKmByIndexRef.current.has(activeIdx)) {
      phaseEndDistanceKmByIndexRef.current.set(activeIdx, distanceKm);
    }
    // Backfill missing block end distances from the next phase start so cool-down
    // km is not attributed to the prior distance block in the summary.
    for (const phase of planStructure.phases) {
      if ((phase.distanceKm ?? 0) <= 0) continue;
      if (phaseEndDistanceKmByIndexRef.current.has(phase.index)) continue;
      const next = planStructure.phases.find((p) => p.index > phase.index);
      if (!next) continue;
      const nextStart = phaseStartDistanceKmByIndexRef.current.get(next.index);
      if (nextStart != null) {
        phaseEndDistanceKmByIndexRef.current.set(phase.index, nextStart);
      }
    }
    const coachGuideReport: CoachGuidePerformanceReport | null = coachModeActive
      ? buildCoachGuidePerformanceReport({
          planText: settings.coachGuide?.plan ?? '',
          structure: planStructure,
          elapsedSeconds: stopElapsed,
          distanceKm,
          phaseEndElapsedByIndex: phaseEndElapsedSecondsByIndexRef.current,
          phaseStartDistanceKmByIndex: phaseStartDistanceKmByIndexRef.current,
          phaseEndDistanceKmByIndex: phaseEndDistanceKmByIndexRef.current,
          hrSamples: hrForReport,
          hrZones: hrZoneSettings.zones,
          runStartMs: startTimeRef.current,
        })
      : null;

    const summary = {
      type: 'running',
      title: defaultTitleWithKm(distanceKm),
      duration: durationMinutes,
      durationSeconds: stopElapsed,
      distance: parseFloat(distanceKm.toFixed(2)),
      pace: formatPace(paceSeconds),
      paceSeconds,
      // Notes will be added when user submits (can include user notes)
      notes: '',
      coachGuideReport,
      // Extended data for better analysis
      averageSpeedKmh: avgSpeedKmh.toFixed(1),
      estimatedCalories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsRecorded: routePoints.length,
      userWeight: userWeightKg,
      userAge: profile?.birth_date ? new Date().getFullYear() - new Date(profile.birth_date).getFullYear() : undefined,
      userHeight: profile?.height_cm,
      // Heart-rate zone summary for after-run screen + Arnold analysis.
      heartRate: (() => {
        const runEnd = Date.now();
        const hr = filterHrSamplesToRunWindow(hrSamplesRef.current, runEnd);
        const watchZones = watchZoneSecondsRef.current;
        const watchZoneTotal = watchZones
          ? Object.values(watchZones).reduce((a, b) => a + (b ?? 0), 0)
          : 0;

        let buckets = hr.length > 0
          ? bucketHrZones(hr, hrZoneSettings.zones)
          : { secondsInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 }, percentInZone: { z1: 0, z2: 0, z3: 0, z4: 0, z5: 0 }, totalSeconds: 0 };

        if (buckets.totalSeconds < MIN_HR_TRACKED_SECONDS && watchZoneTotal >= MIN_HR_TRACKED_SECONDS && watchZones) {
          buckets = bucketsFromZoneSeconds(watchZones, hrZoneSettings.zones);
        }

        const hasEnoughSamples = hr.length >= MIN_HR_SAMPLES_FOR_SUMMARY;
        const hasEnoughBuckets = buckets.totalSeconds >= MIN_HR_TRACKED_SECONDS;
        if (!hasEnoughSamples && !hasEnoughBuckets) return null;

        const bpms = hr.map((s) => s.bpm);
        const avgFromSamples = bpms.length > 0
          ? Math.round(bpms.reduce((a, b) => a + b, 0) / bpms.length)
          : null;
        return {
          maxHr: hrZoneSettings.maxHr,
          maxHrSource: hrZoneSettings.source,
          targetZone,
          avgBpm: avgFromSamples,
          minBpm: bpms.length > 0 ? Math.min(...bpms) : null,
          peakBpm: bpms.length > 0 ? Math.max(...bpms) : null,
          sampleCount: hr.length,
          samples: hr,
          zones: hrZoneSettings.zones,
          buckets,
        };
      })(),
      startTime: new Date(startTimeRef.current).toISOString(),
      endTime: new Date().toISOString(),
      startLocation: routePoints.length > 0 ? {
        lat: routePoints[0].lat,
        lng: routePoints[0].lng
      } : null,
      endLocation: routePoints.length > 0 ? {
        lat: routePoints[routePoints.length - 1].lat,
        lng: routePoints[routePoints.length - 1].lng
      } : null,
      kmSplits: [] as { km: number; time: string; pace: string; isPartial?: boolean }[],
      segments: runSegmentsRef.current.map((seg) => ({
        index: seg.index,
        durationSeconds: seg.durationSeconds,
        distanceMeters: seg.distanceMeters,
        avgHeartRateBpm: seg.avgHeartRateBpm,
      })),
      _ts: Date.now() // Timestamp for freshness check
    };
    
    setWorkoutSummary(summary);
    workoutSummaryRef.current = summary;
    
    // Calculate distance splits (1 km or 1 mi) with pace stored as sec/km for display conversion
    const splits: { km: number; time: string; pace: string; isPartial?: boolean }[] = [];
    let prevSeconds = 0;
    const splitMeters = splitLengthMeters(units);
    const fullSplits = Math.floor(stopDistanceM / splitMeters);
    const splitLenKm = splitMeters / 1000;
    
    console.log('[Splits] Calculating - distanceKm:', distanceKm, 'fullSplits:', fullSplits, 'splitTimesRef:', splitTimesRef.current);
    
    // Add recorded splits (full km/mi completed)
    for (const split of splitTimesRef.current) {
      const splitSeconds = split.elapsedSeconds - prevSeconds;
      const paceSecPerKm = splitSecondsToPaceSecPerKm(splitSeconds, units);
      splits.push({
        km: split.km,
        time: formatTime(split.elapsedSeconds),
        pace: formatPace(paceSecPerKm),
      });
      prevSeconds = split.elapsedSeconds;
    }
    // Ensure every full split is shown (fill gaps if any were missed)
    for (let n = 1; n <= fullSplits; n++) {
      if (!splits.some(s => s.km === n)) {
        const avgPace = distanceKm > 0 ? stopElapsed / distanceKm : 0;
        const estimatedCumulative = avgPace * splitLenKm * n;
        splits.push({
          km: n,
          time: formatTime(estimatedCumulative),
          pace: formatPace(avgPace),
        });
      }
    }
    splits.sort((a, b) => a.km - b.km);
    
    // Partial last split — always include when distance remains.
    const remainingUnits = stopDistanceM / splitMeters - fullSplits;
    const lastRecordedSplitTime = splits.length > 0 ? prevSeconds : 0;
    let remainingSeconds = stopElapsed - lastRecordedSplitTime;
    if (remainingUnits >= 0.01 && remainingSeconds <= 0) {
      const avgPace = distanceKm > 0 ? stopElapsed / distanceKm : 0;
      remainingSeconds = Math.max(1, Math.round(avgPace * remainingUnits * splitLenKm));
    }
    
    console.log('[Splits] Remaining - remainingUnits:', remainingUnits, 'remainingSeconds:', remainingSeconds);
    
    if (remainingUnits >= 0.01) {
      const remainingKm = remainingUnits * splitLenKm;
      const estimatedPacePerKm = remainingKm > 0 ? remainingSeconds / remainingKm : 0;
      splits.push({
        km: Math.round(remainingUnits * 100) / 100,
        time: formatTime(stopElapsed),
        pace: formatPace(estimatedPacePerKm),
        isPartial: true
      });
    } else if (splits.length === 0 && distanceKm > 0) {
      const avgPace = stopElapsed / distanceKm;
      const displayUnits = stopDistanceM / splitMeters;
      splits.push({
        km: Math.round(displayUnits * 100) / 100,
        time: formatTime(stopElapsed),
        pace: formatPace(avgPace),
        isPartial: true
      });
    }
    
    console.log('[Splits] Final result:', splits);
    setKmSplits(splits);
    const summaryWithSplits = {
      ...summary,
      kmSplits: splits,
      segments: runSegmentsRef.current.map((seg) => ({
        index: seg.index,
        durationSeconds: seg.durationSeconds,
        distanceMeters: seg.distanceMeters,
        avgHeartRateBpm: seg.avgHeartRateBpm,
      })),
    };
    workoutSummaryRef.current = summaryWithSplits;
    setWorkoutSummary(summaryWithSplits);
    // Persist metrics so Save can never write 0 km / 0:00 over a real run.
    const metrics = {
      distanceKm: Number(summaryWithSplits.distance) || distanceKm,
      durationSec: Math.max(1, Number(summaryWithSplits.durationSeconds) || stopElapsed),
      calories: Number(summaryWithSplits.estimatedCalories) || estimatedCalories,
    };
    savedRunMetricsRef.current = metrics;
    try {
      sessionStorage.setItem(
        `straiv_run_metrics_${startTimeRef.current}`,
        JSON.stringify(metrics),
      );
    } catch {
      /* ignore */
    }
    setRunSegments(
      runSegmentsRef.current.map((seg) => ({
        ...seg,
        time: formatTime(Math.round(seg.durationSeconds)),
      })),
    );
    
    // Set default run title with date
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    setRunTitle(defaultTitleWithDate(dateStr));
    
    // Reset feeling emoji
    setFeelingEmoji("");
    setRunNotes("");
    
    // Natural speech for run summary — max 2 decimal places
    let distanceSpeech: string;
    if (distanceKm >= 1) {
      const rounded = Math.round(distanceKm * 100) / 100;
      distanceSpeech = `${rounded} kilometer${rounded === 1 ? '' : 's'}`;
    } else {
      distanceSpeech = `${Math.round(stopDistanceM)} meters`;
    }
    {
      const finishText = t(
        gpsKind === 'walk'
          ? 'running.gpsActivity.finishSpeech_walk'
          : gpsKind === 'hike'
            ? 'running.gpsActivity.finishSpeech_hike'
            : 'running.gpsActivity.finishSpeech_run',
        {
          distance: distanceSpeech,
          time: formatTimeForSpeech(stopElapsed),
        },
      );
      void speakNativeNow(finishText, (settingsRef.current.audioVoice || 'female') as 'male' | 'female');
    }
    Analytics.runningAudioAnnouncementPlayed('finish');
    
    Analytics.runningWorkoutCompleted({
      distanceKm: parseFloat(distanceKm.toFixed(2)),
      durationSeconds: stopElapsed,
      avgPace: formatPace(paceSeconds),
      calories: estimatedCalories,
      pauseCount: pauseCount,
      gpsPointsCount: routePoints.length,
      splitsCount: splits.length
    });

    if (settingsRef.current.coachGuide?.enabled) {
      Analytics.guidedRunCompleted({
        distance_km: parseFloat(distanceKm.toFixed(2)),
        duration_seconds: stopElapsed,
        frequency: settingsRef.current.coachGuide.frequency,
        plan_source: settingsRef.current.coachGuide.source,
      });
    }
    
    Analytics.workoutCompleted('running', {
      duration_minutes: durationMinutes,
      distance_km: distanceKm,
      is_gps_tracked: true,
      pause_count: pauseCount,
      calories: estimatedCalories
    });
    
    Analytics.runningSummaryOpened(distanceKm, stopElapsed);
    } catch (err) {
      console.error('[Run] Failed to build run summary:', err);
      const fallbackKm = stopDistanceM / 1000;
      const fallback = {
        type: 'running',
        title: defaultTitleWithKm(fallbackKm),
        duration: Math.round(stopElapsed / 60),
        durationSeconds: stopElapsed,
        distance: parseFloat(fallbackKm.toFixed(2)),
        pace: '--:--',
        notes: '',
        estimatedCalories: 0,
        pauseCount,
        gpsPointsRecorded: routePoints.length,
        _ts: Date.now(),
        _fallback: true,
      };
      workoutSummaryRef.current = fallback;
      setWorkoutSummary(fallback);
      setKmSplits([]);
    } finally {
      if (stopSafeguard) window.clearTimeout(stopSafeguard);
      setIsStoppingRun(false);
      setShowRunSummary(true);
      if (!runDiscardedRef.current) {
        const savePromise = autoSaveRunWorkoutRef.current(workoutSummaryRef.current);
        autoSavePromiseRef.current = savePromise;
        void savePromise;
      }
    }
  };

  const handleConfirmStopRef = useRef(handleConfirmStop);
  handleConfirmStopRef.current = handleConfirmStop;

  // Live sync from Apple Watch (HR zones, segments, splits, end-run).
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'ios' || !runActive) return;
    let removeMetrics: (() => void) | undefined;
    let removeEnded: (() => void) | undefined;
    let removePause: (() => void) | undefined;
    void addWatchLiveMetricsListener(mergeWatchLiveMetrics).then((fn) => {
      removeMetrics = fn;
    });
    void addWatchPauseListener((paused) => {
      // The watch user paused/resumed — mirror it on the phone without
      // re-broadcasting (broadcast=false) so we don't bounce the command back.
      if (paused) handlePauseRef.current(false);
      else handleResumeRef.current(false);
    }).then((fn) => {
      removePause = fn;
    });
    void addWatchRunEndedListener((workout) => {
      if (runEndedRef.current || !runActiveRef.current) return;
      if (!workout) return;
      const runStartMs = startTimeRef.current;
      const workoutStart = new Date(workout.startedAt).getTime();
      if (
        runStartMs > 0 &&
        !Number.isNaN(workoutStart) &&
        Math.abs(workoutStart - runStartMs) > 180_000
      ) {
        console.warn('[Run] Ignoring stale watchRunEnded from prior session');
        return;
      }
      const durationSec = Number(workout.durationSeconds) || 0;
      const distanceM = Number(workout.distanceMeters) || 0;
      // Only ignore discard/stale noise (~0.07s kills). Real Watch End at any time is fine.
      if (durationSec < 3 && distanceM < 5) {
        console.warn('[Run] Ignoring trivial watchRunEnded', { durationSec, distanceM });
        return;
      }
      pendingWatchWorkoutRef.current = workout;
      mergeWatchCompletedWorkout(workout, elapsedSecondsRef.current, totalDistanceRef.current);
      Analytics.watchRunSessionEnded(true);
      watchEndedStopRef.current = true;
      stopConfirmedRef.current = true;
      void handleConfirmStopRef.current();
    }).then((fn) => {
      removeEnded = fn;
    });
    const pollInterval = window.setInterval(() => {
      void getWatchLiveMetrics().then((metrics) => {
        if (metrics) mergeWatchLiveMetrics(metrics);
      });
    }, 1500);
    return () => {
      removeMetrics?.();
      removeEnded?.();
      removePause?.();
      window.clearInterval(pollInterval);
    };
  }, [runActive, mergeWatchLiveMetrics, mergeWatchCompletedWorkout]);
  
  // Feeling icons (using Lucide icons instead of emojis)
  const feelingOptions = useMemo(() => [
    { id: 'exhausted', icon: Frown, label: t('running.summary.feeling.exhausted'), color: 'text-red-400' },
    { id: 'tough', icon: Meh, label: t('running.summary.feeling.tough'), color: 'text-orange-400' },
    { id: 'good', icon: Smile, label: t('running.summary.feeling.good'), color: 'text-yellow-400' },
    { id: 'strong', icon: Zap, label: t('running.summary.feeling.strong'), color: 'text-primary' },
    { id: 'amazing', icon: Flame, label: t('running.summary.feeling.amazing'), color: 'text-primary' },
  ], [t]);
  
  // Handle save run summary (step 1 → step 2) - also save to workout history
  const handleSaveRunSummary = async () => {
    const runSaveSessionKey = `straiv_run_saved_${startTimeRef.current}`;
    const runExerciseSessionKey = `straiv_run_exercise_${startTimeRef.current}`;

    if (runSaveInFlightRef.current) return;
    runSaveInFlightRef.current = true;
    setIsSavingRun(true);

    const SAVE_DEADLINE_MS = 20_000;
    const saveStartedAt = Date.now();
    const advanceAfterSave = () => {
      runSaveInFlightRef.current = false;
      setIsSavingRun(false);
      setShowRunSummary(false);
      Analytics.runningAnalysisPromptShown();
      setShowAnalyzePrompt(true);
    };

    // Don't wait forever on auto-save — user already finished the run.
    await withPluginTimeout(
      autoSavePromiseRef.current ?? Promise.resolve(),
      12_000,
      undefined,
    ).catch(() => undefined);

    let existingWorkoutId = savedWorkoutIdRef.current;
    let existingExerciseId = savedExerciseIdRef.current;
    if (!existingWorkoutId) {
      try {
        existingWorkoutId = sessionStorage.getItem(runSaveSessionKey);
        existingExerciseId = sessionStorage.getItem(runExerciseSessionKey);
      } catch {
        /* sessionStorage unavailable */
      }
    }

    // Log summary saved with details
    const todayTitle = defaultTitleWithDate(
      new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    );
    Analytics.runningSummarySaved(
      runNotes.trim().length > 0,
      feelingEmoji.length > 0,
      runTitle.trim() !== todayTitle
    );

    // Clear the active running session from localStorage
    localStorage.removeItem('activeRunningSession');

    let saveSucceeded = false;

    const enrichedRunSegments = enrichSegmentsWithAvgHr(
      runSegmentsRef.current,
      startTimeRef.current,
      hrSamplesRef.current,
    ).map((seg) => ({
      ...seg,
      time: formatTime(Math.round(seg.durationSeconds)),
    }));

    try {
      const summary = workoutSummaryRef.current ?? workoutSummary;
      const stashedMetrics = (() => {
        if (savedRunMetricsRef.current) return savedRunMetricsRef.current;
        try {
          const raw = sessionStorage.getItem(`straiv_run_metrics_${startTimeRef.current}`);
          if (!raw) return null;
          return JSON.parse(raw) as { distanceKm: number; durationSec: number; calories: number };
        } catch {
          return null;
        }
      })();
      const summaryDistanceKm = Math.max(
        0,
        Number(summary?.distance) || 0,
        stashedMetrics?.distanceKm || 0,
        totalDistanceRef.current / 1000,
      );
      const summaryDurationSec = Math.max(
        1,
        Number(summary?.durationSeconds) || 0,
        Math.round((Number(summary?.duration) || 0) * 60) || 0,
        stashedMetrics?.durationSec || 0,
        elapsedSecondsRef.current || 0,
        elapsedSeconds || 0,
      );
      const summaryCalories = Math.max(
        0,
        Number(summary?.estimatedCalories) || 0,
        stashedMetrics?.calories || 0,
      );
      const workoutTitle =
        runTitle.trim() ||
        defaultTitleWithKm(summaryDistanceKm);
      const workoutNotes = [
        runNotes.trim(),
        feelingEmoji ? `Feeling: ${feelingOptions.find(f => f.id === feelingEmoji)?.label || ''}` : '',
        kmSplits.length > 0
          ? `Splits: ${kmSplits.map((s) => {
              const parts = s.pace.split(':').map((p) => parseInt(p, 10));
              const paceSec =
                parts.length >= 2 && parts.every((n) => Number.isFinite(n))
                  ? parts[0] * 60 + parts[1]
                  : 0;
              return `${s.km}${splitUnitShort(units)}: ${formatPaceDisplay(paceSec, units)}${paceUnitLabel(units)}`;
            }).join(', ')}`
          : ''
      ].filter(Boolean).join('\n');
      const exerciseNotesJson = buildGpsRunExerciseNotesJson({
        workoutSummary: summary ?? {
          distance: summaryDistanceKm,
          durationSeconds: summaryDurationSec,
          estimatedCalories: summaryCalories,
        },
        kmSplits,
        enrichedRunSegments,
        routePoints,
        watchZoneSeconds: watchZoneSecondsRef.current,
        pauseCount,
        feelingEmoji,
        autoSaved: false,
        slim: true,
      });
      const workoutPayload = {
        title: workoutTitle,
        description: workoutDescriptionLine(
          summaryDistanceKm,
          formatTime(summaryDurationSec)
        ),
        duration_minutes: Math.max(1, Math.round(summaryDurationSec / 60)),
        calories_burned: summaryCalories,
        difficulty_level: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Moderate',
        notes: workoutNotes,
      };
      // Never write zeros — omit fields rather than wiping a good auto-save.
      const exercisePayload: {
        notes: string;
        duration_seconds?: number;
        distance_km?: number;
      } = {
        notes: exerciseNotesJson,
      };
      if (summaryDurationSec > 1) exercisePayload.duration_seconds = summaryDurationSec;
      if (summaryDistanceKm > 0) exercisePayload.distance_km = summaryDistanceKm;

      const remainingMs = () => Math.max(3_000, SAVE_DEADLINE_MS - (Date.now() - saveStartedAt));

      if (existingWorkoutId) {
        if (!existingExerciseId) {
          existingExerciseId = await resolveRunExerciseId(existingWorkoutId);
        }

        // skipRefresh: never block Save on refetching the full workout list.
        const { error: workoutError } = await withPluginTimeout(
          updateWorkout(existingWorkoutId, workoutPayload, { skipRefresh: true }),
          remainingMs(),
          { error: new Error('timeout') },
        );
        if (workoutError) {
          console.error('Error updating auto-saved workout:', workoutError);
          // Workout row already exists from auto-save — still let the user leave.
          if (existingWorkoutId) {
            toast.success(t('running.summary.toast.saveSuccess'));
            saveSucceeded = true;
          } else {
            toast.error(t('running.summary.toast.saveFailedConnection'));
            runSaveInFlightRef.current = false;
            setIsSavingRun(false);
            return;
          }
        } else if (existingExerciseId) {
          const { error: exerciseError } = await withPluginTimeout(
            updateExercise(existingExerciseId, exercisePayload, { skipRefresh: true }),
            remainingMs(),
            { error: new Error('timeout') },
          );
          if (exerciseError) {
            console.error('Error updating run details:', exerciseError);
            // Retry once — detail row is what shows distance/time in history.
            await withPluginTimeout(
              updateExercise(existingExerciseId, exercisePayload, { skipRefresh: true }),
              8_000,
              { error: new Error('timeout') },
            );
          }
          saveSucceeded = true;
          toast.success(t('running.summary.toast.saveSuccess'));
        } else {
          const tryAddExercise = () =>
            withPluginTimeout(
              addExerciseToWorkout(existingWorkoutId, {
                name: gpsExerciseName,
                muscle_groups: ['legs', 'cardiovascular'],
                category: 'Cardio',
                duration_seconds: exercisePayload.duration_seconds ?? summaryDurationSec,
                distance_km: exercisePayload.distance_km ?? summaryDistanceKm,
                notes: exercisePayload.notes,
              }, { skipRefresh: true }),
              remainingMs(),
              { data: null, error: new Error('timeout') },
            );
          let { data: exercise, error: exerciseError } = await tryAddExercise();
          if (exerciseError || !exercise?.id) {
            console.error('Error adding run details to existing workout:', exerciseError);
            ({ data: exercise, error: exerciseError } = await tryAddExercise());
          }
          existingExerciseId = exercise?.id ?? null;
          saveSucceeded = true;
          toast.success(t('running.summary.toast.saveSuccess'));
        }

        if (!workoutError) {
          savedWorkoutIdRef.current = existingWorkoutId;
          savedExerciseIdRef.current = existingExerciseId;
          if (!saveSucceeded) {
            saveSucceeded = true;
            toast.success(t('running.summary.toast.saveSuccess'));
          }
          console.log('Workout updated in history:', existingWorkoutId);
        }
      } else {
        const { data: workout, error: workoutError } = await withPluginTimeout(
          createWorkout({
            ...workoutPayload,
            workout_type: gpsWorkoutType,
            started_at: new Date(startTimeRef.current).toISOString(),
            completed_at: new Date().toISOString(),
          }),
          remainingMs(),
          { data: null, error: new Error('timeout') },
        );

        if (workoutError || !workout?.id) {
          console.error('Error creating workout:', workoutError);
          toast.error(t('running.summary.toast.saveFailedConnection'));
          runSaveInFlightRef.current = false;
          setIsSavingRun(false);
          return;
        }

        savedWorkoutIdRef.current = workout.id;
        try {
          sessionStorage.setItem(runSaveSessionKey, workout.id);
        } catch {
          /* sessionStorage unavailable */
        }

        const tryAddExercise = () =>
          withPluginTimeout(
            addExerciseToWorkout(workout.id, {
              name: gpsExerciseName,
              muscle_groups: ['legs', 'cardiovascular'],
              category: 'Cardio',
              duration_seconds: exercisePayload.duration_seconds ?? summaryDurationSec,
              distance_km: exercisePayload.distance_km ?? summaryDistanceKm,
              notes: exercisePayload.notes,
            }, { skipRefresh: true }),
            remainingMs(),
            { data: null, error: new Error('timeout') },
          );
        let { data: exercise, error: exerciseError } = await tryAddExercise();
        if (exerciseError || !exercise?.id) {
          console.error('Error adding run details (workout kept):', exerciseError);
          ({ data: exercise, error: exerciseError } = await tryAddExercise());
        }

        saveSucceeded = true;
        savedExerciseIdRef.current = exercise?.id ?? null;
        try {
          if (exercise?.id) {
            sessionStorage.setItem(runExerciseSessionKey, exercise.id);
          }
        } catch {
          /* sessionStorage unavailable */
        }
        console.log('Workout saved to history:', workout.id);
        toast.success(t('running.summary.toast.saveSuccess'));
      }
    } catch (error) {
      console.error('Error saving workout to history:', error);
      // If auto-save already created a workout, never trap the user here.
      if (savedWorkoutIdRef.current || existingWorkoutId) {
        saveSucceeded = true;
        toast.success(t('running.summary.toast.saveSuccess'));
      } else {
        toast.error(t('running.summary.toast.saveGenericError'));
        runSaveInFlightRef.current = false;
        setIsSavingRun(false);
        return;
      }
    }

    if (!saveSucceeded && (savedWorkoutIdRef.current || existingWorkoutId)) {
      saveSucceeded = true;
    }
    if (!saveSucceeded) {
      runSaveInFlightRef.current = false;
      setIsSavingRun(false);
      return;
    }

    advanceAfterSave();
  };
  
  // Handle delete run - show confirmation first
  const handleDeleteRun = () => {
    setShowDeleteConfirm(true);
  };
  
  // Confirm delete run — remove auto-saved workout if present, then leave without saving.
  const confirmDeleteRun = async () => {
    setShowDeleteConfirm(false);
    runDiscardedRef.current = true;

    await autoSavePromiseRef.current?.catch(() => undefined);

    const runSaveSessionKey = `straiv_run_saved_${startTimeRef.current}`;
    const runExerciseSessionKey = `straiv_run_exercise_${startTimeRef.current}`;
    const workoutId = savedWorkoutIdRef.current;

    if (workoutId) {
      const { error } = await deleteWorkout(workoutId);
      if (error) {
        toast.error(t('running.summary.toast.saveFailedConnection'));
        return;
      }
      savedWorkoutIdRef.current = null;
      savedExerciseIdRef.current = null;
    }

    try {
      sessionStorage.removeItem(runSaveSessionKey);
      sessionStorage.removeItem(runExerciseSessionKey);
    } catch {
      /* sessionStorage unavailable */
    }

    setShowRunSummary(false);
    toast.success(t('running.summary.toast.runDeleted'));
    Analytics.runningSummaryDeleted();
    navigate('/workouts');
  };
  
  // Handle analyze with Arnold
  const handleAnalyzeWithArnold = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis requested
    Analytics.runningAnalysisRequested(
      workoutSummary?.distance || 0,
      elapsedSeconds
    );
    
    // Build splits string for Arnold (display units)
    const splitLabel = splitUnitShort(units);
    const splitsText = kmSplits.length > 0 
      ? kmSplits.map((s) => {
          const parts = s.pace.split(':').map((p) => parseInt(p, 10));
          const paceSec =
            parts.length >= 2 && parts.every((n) => Number.isFinite(n))
              ? parts[0] * 60 + parts[1]
              : 0;
          return `${s.km}${splitLabel}: ${formatPaceDisplay(paceSec, units)}${paceUnitLabel(units)}`;
        }).join(', ')
      : 'No splits recorded';
    
    // Add all data to the summary for Arnold
    const heartRate = workoutSummary?.heartRate ?? null;
    const heartRateCoachText = formatHeartRateSummaryForCoach(heartRate);

    const summaryWithAllData = {
      ...workoutSummary,
      _ts: Date.now(),
      type: 'running' as const,
      durationSeconds: workoutSummary?.durationSeconds ?? elapsedSeconds,
      title:
        runTitle.trim() ||
        defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
      notes: runNotes.trim() || '',
      feeling: feelingOptions.find(f => f.id === feelingEmoji)?.label || 'Not specified',
      feelingId: feelingEmoji,
      splits: splitsText,
      kmSplits: kmSplits,
      segments: runSegments,
      segmentsText: runSegments.length > 0
        ? runSegments.map((s) => {
            const distKm = s.distanceMeters > 0 ? s.distanceMeters / 1000 : 0;
            const paceSec = s.distanceMeters > 0 && s.durationSeconds > 0
              ? s.durationSeconds / (s.distanceMeters / 1000)
              : 0;
            const distText = distKm > 0
              ? `${formatDistanceValue(distKm, units)} ${distanceUnitShort(units)}`
              : 'n/a';
            const pace = paceSec > 0
              ? `${formatPaceDisplay(paceSec, units)}${paceUnitLabel(units)}`
              : `--:--${paceUnitLabel(units)}`;
            return `Segment ${s.index}: ${s.time}, ${distText}, ${pace}`;
          }).join('; ')
        : '',
      userNotes: runNotes.trim(),
      heartRate,
      heartRateCoachText,
    };

    if (user?.id && heartRateCoachText) {
      try {
        localStorage.setItem(
          'arnoldLastRunSummary',
          JSON.stringify({
            userId: user.id,
            _ts: Date.now(),
            title: summaryWithAllData.title,
            coachText: heartRateCoachText.trim(),
          }),
        );
      } catch {
        /* ignore */
      }
    }
    
    // Store the workout summary for Arnold to analyze
    try {
      localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(summaryWithAllData));
      console.log('Saved running analysis to localStorage:', summaryWithAllData);
    } catch (e) {
      console.warn('Failed to save workout analysis to localStorage:', e);
    }

    // Don't compete with the analysis request on a heavy context RPC + health sync.
    try {
      sessionStorage.setItem(CHAT_DEFER_CONTEXT_REFRESH_KEY, '1');
    } catch {
      /* ignore */
    }
    
    // Navigate to chat with Arnold
    navigate('/chat', {
      state: {
        fromWorkout: true,
        workoutData: summaryWithAllData,
      },
    });
  };
  
  // Handle skip analysis
  const handleSkipAnalysis = () => {
    setShowAnalyzePrompt(false);
    
    // Log analysis skipped
    Analytics.runningAnalysisSkipped();
    
    toast.success(t('running.summary.toast.skipAnalysis'));
    navigate('/workouts');
  };
  
  const distanceKm = totalDistance / 1000;
  const livePaceSeconds = resolveCurrentLivePace({
    watchLive: isWatchDistanceLive(),
    watchLivePace: watchLivePaceRef.current,
    watchRollingPace: watchRollingPaceRef.current,
    gpsWindow: gpsPaceWindowRef.current,
    lastGpsSpeedMps: lastGpsSpeedMpsRef.current,
    lastGpsSpeedAt: lastGpsSpeedAtRef.current,
  });
  const averagePaceSeconds = averagePaceSecondsPerKm(elapsedSeconds, totalDistance);

  const phoneGuideDisplay = useMemo(() => {
    if (!coachModeActive || !settings.coachGuide?.plan.trim()) return null;

    const hr =
      currentBpm != null && currentBpm > 0 ? String(Math.round(currentBpm)) : "--";
    const livePace = formatPaceUnits(livePaceSeconds);

    if (coachPlanCompletedRef.current && coachPlanCompleteAtRef.current) {
      const snap = coachPlanCompleteAtRef.current;
      return {
        isComplete: true,
        headline: t("running.active.guide.workoutComplete", {
          defaultValue: "Workout complete!",
        }),
        completeSummary: `${formatTime(snap.elapsed)} · ${formatKm(snap.distanceKm)}`,
        isRest: false,
        metricMode: "time" as const,
        intoMeters: undefined as number | undefined,
        leftMeters: undefined as number | undefined,
        remainingSec: 0,
        livePace,
        hr,
      };
    }

    let guide: ReturnType<typeof buildWatchCoachGuidePayload> = null;
    try {
      guide = stabilizeWatchCoachGuidePayload(
        buildWatchCoachGuidePayload({
          plan: settings.coachGuide.plan,
          structure: planStructure,
          elapsedSeconds,
          distanceKm,
          phaseStartDistanceKmByIndex: phaseStartDistanceKmByIndexRef.current,
          phaseEndElapsedSecondsByIndex: phaseEndElapsedSecondsByIndexRef.current,
          lastPhaseIndexRef: lastResolvedPhaseIndexRef,
          phaseStartElapsedSecondsByIndex: phaseStartElapsedSecondsByIndexRef.current,
        }),
        lastResolvedPhaseIndexRef.current,
        maxDistanceIntoBlockByPhaseRef,
      );
    } catch (err) {
      console.warn("Failed to build phone AI guide display:", err);
    }

    const remainingSec =
      guide?.blockEndsAtRunElapsed != null
        ? Math.max(0, Math.round(guide.blockEndsAtRunElapsed - elapsedSeconds))
        : Math.max(0, Math.round(guide?.remainingSeconds ?? 0));

    return {
      isComplete: !!guide?.isComplete,
      headline:
        guide?.headline?.trim() ||
        t("running.active.guide.title", { defaultValue: "AI Guide" }),
      completeSummary: guide?.completeSummary,
      isRest: !!guide?.isRest,
      metricMode: guide?.metricMode ?? "time",
      intoMeters: guide?.distanceIntoBlockMeters,
      leftMeters: guide?.remainingDistanceMeters,
      remainingSec,
      livePace,
      hr,
    };
  }, [
    coachModeActive,
    settings.coachGuide?.plan,
    planStructure,
    elapsedSeconds,
    distanceKm,
    currentBpm,
    livePaceSeconds,
    activePhaseIndex,
    formatPaceUnits,
    formatKm,
    t,
  ]);
  
  // Calculate real-time calories using MET formula
  const userWeightKg = profile?.weight_kg || 70;
  const avgSpeedKmh = elapsedSeconds > 0 ? distanceKm / (elapsedSeconds / 3600) : 0;
  let currentMet = 6.0;
  if (gpsKind === 'walk') {
    if (avgSpeedKmh >= 6.5) currentMet = 5.0;
    else if (avgSpeedKmh >= 5) currentMet = 3.8;
    else currentMet = 3.3;
  } else if (gpsKind === 'hike') {
    if (avgSpeedKmh >= 5.5) currentMet = 9.0;
    else if (avgSpeedKmh >= 4) currentMet = 7.0;
    else currentMet = 6.0;
  } else {
    if (avgSpeedKmh >= 14) currentMet = 13.5;
    else if (avgSpeedKmh >= 12) currentMet = 11.5;
    else if (avgSpeedKmh >= 10) currentMet = 9.8;
    else if (avgSpeedKmh >= 8) currentMet = 8.3;
  }
  const currentCalories = Math.round(currentMet * userWeightKg * (elapsedSeconds / 3600));
  
  // Route data for display - memoized so map markers don't flicker when runTitle/runNotes change
  const routeSegmentsForMap = useMemo<[number, number][][]>(() =>
    routeSegments
      .filter(seg => seg.length >= 2)
      .map(seg => seg.map(p => [p.lat, p.lng] as [number, number])),
    [routeSegments]
  );
  const routeLine = useMemo<[number, number][]>(() =>
    routePoints.map(p => [p.lat, p.lng]),
    [routePoints]
  );
  // Stable center for summary map - prevents PlatformMap re-render when typing
  const mapCenterForSummary = useMemo<[number, number] | null>(() =>
    routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null,
    [routeLine]
  );
  const coloredRouteSegmentsForSummary = useMemo(
    () => {
      const hr = workoutSummary?.heartRate;
      if (!hr?.samples?.length || routePoints.length < 2) return [];
      const runStartMs = workoutSummary?.startTime
        ? new Date(workoutSummary.startTime as string).getTime()
        : startTimeRef.current;
      const runDurationSec = Number(workoutSummary?.durationSeconds) || elapsedSeconds;
      return buildZoneColoredRouteSegments(routePoints, hr.samples, hr.zones, {
        runStartMs,
        runDurationSec,
      });
    },
    [workoutSummary?.heartRate, workoutSummary?.startTime, workoutSummary?.durationSeconds, routePoints, elapsedSeconds],
  );
  
  // Auto-follow: Update map center when user position changes (if auto-follow is on)
  useEffect(() => {
    if (currentPosition && isMapCentered) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
  }, [currentPosition, isMapCentered]);
  
  // Handle recenter map - go back to user's position and enable auto-follow
  const handleRecenterMap = () => {
    if (currentPosition) {
      setMapCenter([...currentPosition]); // Create new array to trigger re-render
    }
    setIsMapCentered(true); // Enable auto-follow, hide recenter button
    Analytics.runningMapRecentered();
  };
  
  // Handle map interaction (user scrolled/panned away) - disable auto-follow
  const handleMapInteraction = useCallback(() => {
    setIsMapCentered(false); // Disable auto-follow, show recenter button
    Analytics.runningMapScrolled();
  }, []);
  
  return (
    <div 
      className="fixed inset-0 bg-background flex flex-col"
      style={showRunSummary ? { pointerEvents: 'none' } : undefined}
    >
      {/* Map Background — kept mounted so toggling back is instant */}
      <div className={`absolute inset-0 z-0 ${showPhoneGuide ? "opacity-0 pointer-events-none" : ""}`}>
        {!currentPosition ? (
          <div className="h-full w-full flex items-center justify-center bg-background">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-primary mx-auto animate-pulse" />
              <div className="text-lg text-primary mt-4">{t('running.active.gpsAcquiring')}</div>
              <div className="text-sm text-muted-foreground mt-2">{t('running.active.pleaseWait')}</div>
            </div>
          </div>
        ) : (
          <PlatformMap 
            center={mapCenter || currentPosition}
            userLocation={currentPosition}
            routeSegments={routeSegmentsForMap}
            showUserMarker={true}
            showRoute={true}
            className="w-full h-full"
            darkMode={true}
            autoFollow={isMapCentered}
            onUserScroll={handleMapInteraction}
          />
        )}
        
        {/* Gradient overlays for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/70 pointer-events-none" />
      </div>

      {showPhoneGuide ? (
        <div
          className="relative z-10 flex-1 flex flex-col min-h-0 px-4"
          style={{ paddingTop: `calc(${SAFE_AREA.top} + 70px)` }}
        >
          <div className="bg-background/95 backdrop-blur-md rounded-2xl p-5 shadow-lg border border-border/30 flex-1 min-h-0 flex flex-col overflow-y-auto">
            <div className="text-center mb-3">
              <div className="text-6xl font-mono font-bold text-foreground tabular-nums leading-none">
                {formatTime(elapsedSeconds)}
              </div>
              <div className="text-sm text-muted-foreground mt-2 uppercase tracking-wide">
                {isPaused ? t('running.active.statusPaused') : t('running.active.statusRunning')}
                {" · "}
                {t('running.active.guide.totalTime', { defaultValue: 'Total time' })}
              </div>
            </div>

            {phoneGuideDisplay?.isComplete ? (
              <div className="text-center mt-4 space-y-3">
                <div className="text-2xl font-extrabold text-primary leading-tight">
                  {phoneGuideDisplay.headline}
                </div>
                {phoneGuideDisplay.completeSummary && (
                  <div className="text-lg font-semibold font-mono text-foreground">
                    {phoneGuideDisplay.completeSummary}
                  </div>
                )}
                <p className="text-sm text-muted-foreground">
                  {t('running.active.guide.coolDown', {
                    defaultValue: 'Great work — cool down and recover well.',
                  })}
                </p>
              </div>
            ) : (
              <>
                <div
                  className={`text-[1.65rem] font-extrabold text-center leading-tight mb-4 ${
                    phoneGuideDisplay?.isRest ? "text-yellow-400" : "text-primary"
                  }`}
                >
                  {phoneGuideDisplay?.headline ||
                    t('running.active.guide.title', { defaultValue: 'AI Guide' })}
                </div>

                {phoneGuideDisplay?.metricMode === "distance" ? (
                  <>
                    {phoneGuideDisplay.intoMeters != null && (
                      <PhoneGuideMetric
                        value={formatMeters(phoneGuideDisplay.intoMeters)}
                        label={t('running.active.guide.inBlock', { defaultValue: 'In\nblock' })}
                      />
                    )}
                    {phoneGuideDisplay.leftMeters != null && phoneGuideDisplay.leftMeters > 0 && (
                      <PhoneGuideMetric
                        value={formatMeters(phoneGuideDisplay.leftMeters)}
                        label={t('running.active.guide.blockLeft', { defaultValue: 'Block\nleft' })}
                        accent
                      />
                    )}
                  </>
                ) : (
                  (phoneGuideDisplay?.remainingSec ?? 0) > 0 && (
                    <PhoneGuideMetric
                      value={formatTime(phoneGuideDisplay!.remainingSec)}
                      label={
                        phoneGuideDisplay?.isRest
                          ? t('running.active.guide.restLeft', { defaultValue: 'Rest\nleft' })
                          : t('running.active.guide.timeLeft', { defaultValue: 'Time\nleft' })
                      }
                      accent
                    />
                  )
                )}

                <PhoneGuideMetric
                  value={phoneGuideDisplay?.livePace ?? "--:--"}
                  label={t('running.active.guide.currentPace', { defaultValue: 'Current\npace' })}
                />
                <PhoneGuideMetric
                  value={phoneGuideDisplay?.hr ?? "--"}
                  label={t('running.active.guide.heartRate', { defaultValue: 'Heart\nrate' })}
                />
              </>
            )}

            <Button
              type="button"
              disabled={isLocked}
              onClick={() => setPhoneRunView("map")}
              className={`mt-auto w-full h-12 rounded-2xl text-base font-bold shadow-lg ${
                isLocked ? "opacity-50" : "bg-primary text-primary-foreground hover:bg-primary/90"
              }`}
            >
              <MapIcon className="h-5 w-5 mr-2" />
              {t('running.active.guide.showMap', { defaultValue: 'Show map' })}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Top Stats Bar */}
          <div 
            className="relative z-10 px-4"
            style={{ paddingTop: `calc(${SAFE_AREA.top} + 70px)` }}
          >
            <div className="bg-background/90 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-border/30">
              {/* Timer - Large */}
              <div className="text-center mb-4">
                <div className="text-5xl font-mono font-bold text-foreground">
                  {formatTime(elapsedSeconds)}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {isPaused ? t('running.active.statusPaused') : t('running.active.statusRunning')}
                </div>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Distance */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {formatDistanceValue(distanceKm, units)}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase">{distanceUnitShort(units)}</div>
                </div>
                
                {/* Average pace (map view) */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {formatPaceUnits(averagePaceSeconds)}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase">
                    {t('running.active.avgPace', { defaultValue: 'Avg' })} {paceSuffix}
                  </div>
                </div>
                
                {/* Calories (estimated using MET formula) */}
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">
                    {currentCalories}
                  </div>
                  <div className="text-xs text-muted-foreground uppercase">kcal</div>
                </div>
              </div>
            </div>

            {coachModeActive && (
              <Button
                type="button"
                disabled={isLocked}
                onClick={() => setPhoneRunView("guide")}
                className={`mt-3 w-full h-12 rounded-2xl text-base font-bold shadow-lg ${
                  isLocked ? "opacity-50" : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
              >
                <Zap className="h-5 w-5 mr-2" />
                {t('running.active.guide.showGuide', { defaultValue: 'AI Guide' })}
              </Button>
            )}
          </div>
          
          {/* Spacer to push controls to bottom */}
          <div className="flex-1" />
        </>
      )}
      
      {/* Bottom Controls */}
      <div 
        className="relative z-10 px-4 pb-4"
        style={{ paddingBottom: `calc(${SAFE_AREA.bottom} + 100px)` }}
      >
        {/* Quick Actions - Lock and GPS Signal aligned at bottom */}
        <div className="flex justify-between items-end mb-4">
          {/* Lock Button - aligned with GPS signal */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              const newLockedState = !isLocked;
              setIsLocked(newLockedState);
              if (newLockedState) {
                Analytics.runningScreenLocked();
              } else {
                Analytics.runningScreenUnlocked();
              }
            }}
            className="h-10 w-10 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30"
          >
            {isLocked ? (
              <Lock className="h-4 w-4 text-destructive" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
          </Button>
          
          {/* GPS Signal + Re-center button stacked vertically */}
          <div className="flex flex-col items-center gap-2">
            {/* Re-center button - only visible when user has scrolled away */}
            {!showPhoneGuide && currentPosition && !isMapCentered && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRecenterMap}
                className="h-10 w-10 rounded-full bg-primary/90 backdrop-blur-md shadow-lg border border-border/30 animate-in fade-in-0 zoom-in-95 duration-200"
              >
                <Navigation className="h-4 w-4 text-primary-foreground" />
              </Button>
            )}
            
            {/* GPS Signal Strength */}
            <div className="h-10 px-3 rounded-full bg-background/90 backdrop-blur-md shadow-lg border border-border/30 flex items-center">
              <div className="flex items-end gap-0.5 h-4">
                {[1, 2, 3, 4].map((bar) => {
                  const bars = gpsSignal === 'strong' ? 4 : gpsSignal === 'medium' ? 3 : gpsSignal === 'weak' ? 2 : 1;
                  return (
                    <div
                      key={bar}
                      className={`w-1 rounded-sm transition-all ${
                        bar <= bars ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`}
                      style={{ height: `${bar * 3 + 2}px` }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Controls */}
        <div className="flex items-center justify-center gap-6">
          {/* Stop Button */}
          <Button
            onClick={handleStopClick}
            disabled={isLocked || !isRunning || isStoppingRun}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked ? 'opacity-50' : 'border-destructive text-destructive hover:bg-destructive/10'
            }`}
          >
            <Square className="h-6 w-6" />
          </Button>
          
          {/* Pause/Resume Button */}
          <Button
            onClick={() => (isPaused ? handleResume() : handlePause())}
            disabled={isLocked || !isRunning || isStoppingRun}
            className={`h-20 w-20 rounded-full shadow-glow ${
              isLocked ? 'opacity-50 bg-muted' : 'bg-gradient-primary'
            }`}
          >
            {isPaused ? (
              <Play className="h-8 w-8 text-black ml-1" />
            ) : (
              <Pause className="h-8 w-8 text-black" />
            )}
          </Button>
          
          {/* Segment Button */}
          <Button
            type="button"
            onPointerUp={(e) => {
              e.preventDefault();
              handleMarkSegment();
            }}
            onClick={(e) => {
              e.preventDefault();
              handleMarkSegment();
            }}
            disabled={isLocked || !isRunning || isStoppingRun}
            variant="outline"
            className={`h-16 w-16 rounded-full border-2 ${
              isLocked ? 'opacity-50' : 'border-primary/60 text-primary hover:bg-primary/10'
            }`}
          >
            <Layers className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Lock indicator */}
        {isLocked && (
          <div className="text-center mt-4 text-sm text-muted-foreground">
            {t('running.active.screenLockedHint')}
          </div>
        )}
      </div>
      
      {/* Stopping overlay — blocks Play/Stop while finishing the run */}
      {segmentPopup && createPortal(
        <div className="fixed inset-0 z-[99997] flex items-center justify-center bg-black/80 pointer-events-none">
          <div className="text-center">
            <p className="text-sm font-semibold text-primary mb-1">Segment {segmentPopup.index}</p>
            <p className="text-3xl font-bold font-mono text-white">{formatTime(Math.round(segmentPopup.durationSeconds))}</p>
            <p className="text-lg font-semibold font-mono text-yellow-400 mt-1">
              {(segmentPopup.distanceMeters / 1000).toFixed(2)} km
            </p>
            {segmentPopup.paceSecondsPerKm > 0 && (
              <p className="text-sm font-mono text-white/75 mt-1">
                {formatPaceUnits(segmentPopup.paceSecondsPerKm)}{paceSuffix}
              </p>
            )}
          </div>
        </div>,
        document.body
      )}

      {isStoppingRun && createPortal(
        <div
          className="fixed inset-0 z-[99998] flex flex-col items-center justify-center bg-background/95"
          style={{
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-base font-medium text-foreground">Finishing run…</p>
        </div>,
        document.body
      )}

      {/* Stop Confirmation Dialog - rendered in portal so it blocks ALL interaction including BottomNav */}
      {showStopConfirm && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('running.active.stopDialog.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('running.active.stopDialog.description')}
              </p>
              <div className="flex flex-col gap-2 pt-2">
                <Button variant="outline" onClick={handleCancelStop} className="w-full h-11">
                  {t('running.active.stopDialog.continue')}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleConfirmStop}
                  className="w-full h-11"
                >
                  {t('running.active.stopDialog.confirm')}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Loading overlay - shown while saving workout after Run Summary */}
      {isSavingRun && createPortal(
        <div 
          className="fixed inset-0 z-[100002] flex flex-col items-center justify-center bg-background/95"
          style={{ 
            paddingTop: SAFE_AREA.top, 
            paddingBottom: SAFE_AREA.bottom, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-base font-medium text-foreground">{t('running.summary.savingTitle')}</p>
          <p className="text-sm text-muted-foreground mt-1">{t('running.summary.savingSubtitle')}</p>
          <button
            type="button"
            className="mt-6 text-sm text-muted-foreground underline touch-manipulation px-4 py-3"
            onClick={() => {
              // Never trap the user: if a workout id exists, continue; else cancel spinner.
              runSaveInFlightRef.current = false;
              setIsSavingRun(false);
              if (savedWorkoutIdRef.current) {
                setShowRunSummary(false);
                Analytics.runningAnalysisPromptShown();
                setShowAnalyzePrompt(true);
              }
            }}
          >
            {t('running.summary.continueAnyway', { defaultValue: 'Continue anyway' })}
          </button>
        </div>,
        document.body
      )}

      {/* STEP 1: Run Summary - Compact single-page layout, no scroll needed */}
      {showRunSummary && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex flex-col bg-background overflow-hidden ios-modal-scroll"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: `calc(8px + ${SAFE_AREA.top})`, 
            paddingBottom: `calc(16px + ${SAFE_AREA.bottom})`, 
            paddingLeft: SAFE_AREA.left, 
            paddingRight: SAFE_AREA.right,
            boxSizing: 'border-box',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
          }}
        >
          {/* Delete - fixed top-right, outside scroll flow, guaranteed to receive touches */}
          <button
            type="button"
            onClick={handleDeleteRun}
            className="fixed min-w-[48px] min-h-[48px] flex items-center justify-center text-destructive hover:text-destructive/80 touch-manipulation z-[100001]"
            style={{ 
              top: `calc(8px + ${SAFE_AREA.top})`, 
              right: `calc(8px + ${SAFE_AREA.right})` 
            }}
          >
            <Trash2 className="h-6 w-6" />
          </button>
          <div className="text-center shrink-0 pb-1">
            <div className="flex items-center justify-center">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center">
                <Footprints className="h-5 w-5 text-primary-foreground" />
              </div>
            </div>
            <h2 className="text-base font-bold text-center text-foreground">
              {t('running.summary.title')}
            </h2>
          </div>
          
          <div className="flex-1 min-h-0 flex flex-col px-4">
            {/* Map thumbnail - tap overlay captures all touches, expand icon bottom-right */}
            {routeLine.length > 1 && workoutSummary?.heartRate ? (
              <SummaryMapHrCarousel
                mapCenter={mapCenterForSummary}
                routeSegments={routeSegmentsForMap}
                routePoints={routePoints}
                routeLine={routeLine}
                coloredRouteSegments={coloredRouteSegmentsForSummary}
                onExpandMap={() => setShowExpandedMap(true)}
                onExpandHr={() => setShowExpandedHr(true)}
                onExpandCombined={() => setShowExpandedCombined(true)}
                expandMapAria={t('running.summary.expandMapAria')}
                expandHrAria={t('running.summary.expandHrAria', { defaultValue: 'Expand heart rate zones' })}
                expandCombinedAria={t('running.summary.expandCombinedAria', { defaultValue: 'Expand route and heart rate' })}
                heartRate={workoutSummary.heartRate}
              />
            ) : routeLine.length > 1 ? (
              <div
                className="shrink-0 mb-2 rounded-lg overflow-hidden border border-border/30 h-44 relative cursor-pointer"
                style={{ minHeight: 176 }}
              >
                <PlatformMap
                  key="summary-map"
                  center={mapCenterForSummary}
                  routePoints={routeLine}
                  routeSegments={routeSegmentsForMap}
                  showUserMarker={false}
                  showRoute={true}
                  showStartFinishMarkers={true}
                  showKmMarkers={true}
                  fitToRoute={true}
                  fitPaddingBottom={1.25}
                  autoFollow={false}
                  className="w-full h-full pointer-events-none"
                  darkMode={true}
                  interactive={false}
                  zoom={12}
                />
                {/* Full-map tap overlay - captures touches so expand works on iOS */}
                <div
                  className="absolute inset-0 z-10 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  onClick={() => setShowExpandedMap(true)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setShowExpandedMap(true);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={t('running.summary.expandMapAria')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setShowExpandedMap(true);
                    }
                  }}
                />
                {/* Expand icon - bottom right, visual only */}
                <div
                  className="absolute bottom-2 right-2 z-20 flex items-center justify-center w-10 h-10 rounded-lg bg-black/70 pointer-events-none"
                  aria-hidden
                >
                  <Maximize2 className="h-5 w-5 text-white" />
                </div>
              </div>
            ) : null}
            <div
              className="flex-1 min-h-0 overflow-y-auto space-y-2 py-1 ios-modal-scroll"
              style={{ 
                touchAction: 'pan-y', 
                WebkitOverflowScrolling: 'touch',
                paddingBottom: keyboardHeight > 0 ? keyboardHeight + 48 : undefined,
              } as React.CSSProperties}
            >
            {/* Editable Run Title */}
            <div>
              <label className="text-xs text-muted-foreground mb-0.5 block flex items-center gap-1">
                {t('running.summary.workoutNameLabel')} <Pencil className="h-3 w-3" />
              </label>
              <input
                type="text"
                value={runTitle}
                onChange={(e) => setRunTitle(e.target.value)}
                onBlur={() => {
                  Analytics.runningSummaryTitleEdited();
                }}
                className="w-full text-center font-semibold px-2 py-1.5 rounded-md border border-border bg-background/50 focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                placeholder={t('running.summary.runTitlePlaceholder')}
              />
            </div>
            
            {/* Stats Grid */}
            {workoutSummary && (
              <div className="grid grid-cols-2 gap-2 text-sm bg-muted/30 rounded-lg p-3 border border-border/30">
                <div className="text-center">
                  <div className="font-bold text-base text-primary">{formatKm(Number(workoutSummary.distance) || 0)}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statDistance')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{formatTime(elapsedSeconds)}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statTotalTime')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{formatPaceUnits(Number(workoutSummary.paceSeconds) || 0)}{paceSuffix}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statAvgPacePerKm')}</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-base">{workoutSummary.estimatedCalories}</div>
                  <div className="text-[10px] text-muted-foreground">{t('running.summary.statCalories')}</div>
                </div>
              </div>
            )}

            {workoutSummary?.coachGuideReport && (
              <div className="chat-selectable rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                <div className="text-xs font-semibold text-primary">AI Guide — Plan vs Actual</div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {workoutSummary.coachGuideReport.planText}
                </p>
                <p className="text-xs font-medium">{workoutSummary.coachGuideReport.overallSummary}</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto">
                  {workoutSummary.coachGuideReport.phases.map((phase: CoachGuidePerformanceReport['phases'][number]) => (
                    <div
                      key={`${phase.label}-${phase.kind}`}
                      className="text-[10px] rounded-md bg-background/40 px-2 py-1 border border-border/20"
                    >
                      <div className="font-semibold text-foreground">{phase.label}</div>
                      <div className="text-muted-foreground">Plan: {phase.planned}</div>
                      <div className="text-muted-foreground">Actual: {phase.actual}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Heart rate zones (when no map carousel — otherwise swipe from map) */}
            {workoutSummary?.heartRate && routeLine.length <= 1 ? (
              <HeartRateZoneChart
                zones={workoutSummary.heartRate.zones}
                buckets={workoutSummary.heartRate.buckets}
                maxHr={workoutSummary.heartRate.maxHr}
                maxHrSource={workoutSummary.heartRate.maxHrSource}
                avgBpm={workoutSummary.heartRate.avgBpm}
                peakBpm={workoutSummary.heartRate.peakBpm}
                targetZone={workoutSummary.heartRate.targetZone}
              />
            ) : !workoutSummary?.heartRate ? (
              <div className="rounded-md border border-dashed border-border/60 bg-muted/10 p-3 text-center">
                <div className="text-xs font-medium text-muted-foreground">
                  Heart rate zones
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Connect Apple Health and wear your Apple Watch during your next run to see time spent in each zone.
                </div>
              </div>
            ) : null}

            {/* Km splits (swipe → segments) */}
            <SummarySplitsSegmentsCarousel kmSplits={kmSplits} runSegments={runSegments} />
            
            {/* How did this run feel? */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">{t('running.summary.feelingPrompt')}</label>
              <div className="flex justify-between gap-1.5 touch-manipulation">
                {feelingOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setFeelingEmoji(option.id);
                        Analytics.runningSummaryFeelingSelected(option.id, option.label);
                      }}
                      className={`flex-1 py-2 rounded-md transition-all flex items-center justify-center ${
                        feelingEmoji === option.id 
                          ? 'bg-primary/20 ring-2 ring-primary scale-110' 
                          : 'bg-muted/30 hover:bg-muted/50'
                      }`}
                      title={option.label}
                    >
                      <IconComponent className={`h-5 w-5 ${feelingEmoji === option.id ? option.color : 'text-muted-foreground'}`} />
                    </button>
                  );
                })}
              </div>
              {feelingEmoji && (
                <p className="text-[10px] text-center text-muted-foreground mt-0.5">
                  {feelingOptions.find(f => f.id === feelingEmoji)?.label}
                </p>
              )}
            </div>
            
            {/* Notes - scroll into view when focused, extra padding when keyboard open */}
            <div className="pb-safe">
              <label className="text-xs text-muted-foreground mb-0.5 block">{t('running.summary.notesLabel')}</label>
              <textarea
                value={runNotes}
                onChange={(e) => setRunNotes(e.target.value)}
                onFocus={(e) => {
                  const target = e.target;
                  setTimeout(() => {
                    if (document.activeElement === target) {
                      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, Capacitor.getPlatform() === 'ios' ? 500 : 400);
                }}
                onBlur={() => {
                  if (runNotes.trim().length > 0) {
                    Analytics.runningSummaryNotesAdded(runNotes.trim().length);
                  }
                }}
                placeholder={t('running.summary.notesPlaceholder')}
                style={{ scrollMarginBottom: keyboardHeight > 0 ? keyboardHeight + 80 : 100 }}
                className="w-full min-h-[80px] px-2 py-1.5 text-xs rounded-md border border-border bg-background/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
            
            </div>
            {/* Share & Save Buttons - fixed at bottom, always visible */}
            <div className="flex gap-2 shrink-0 pt-2 touch-manipulation">
              <Button 
                onClick={() => {
                  const distanceKm = workoutSummary?.distance ?? 0;
                  const paceSeconds = distanceKm > 0 ? elapsedSeconds / distanceKm : 0;
                  setWorkoutToShare({
                    id: `run-${Date.now()}`,
                    title:
                      runTitle ||
                      defaultTitleWithKm(Number(workoutSummary?.distance) || 0),
                    duration_minutes: elapsedSeconds / 60,
                    completed_at: new Date().toISOString(),
                    workout_type: gpsWorkoutType.toLowerCase(),
                    distance_km: distanceKm,
                    average_pace: workoutSummary?.pace ?? '--:--',
                    calories_burned: workoutSummary?.estimatedCalories,
                    route_coordinates: routePoints.map(p => ({ lat: p.lat, lng: p.lng })),
                    duration_seconds: elapsedSeconds,
                    pace_seconds_per_km: paceSeconds,
                    splits: kmSplits,
          segments: runSegments.map((s) => ({
            index: s.index,
            durationSeconds: s.durationSeconds ?? 0,
            distanceMeters: s.distanceMeters ?? 0,
          })),
                    heart_rate: workoutSummary?.heartRate
                      ? {
                          zones: workoutSummary.heartRate.zones,
                          buckets: workoutSummary.heartRate.buckets,
                          maxHr: workoutSummary.heartRate.maxHr,
                          avgBpm: workoutSummary.heartRate.avgBpm,
                          peakBpm: workoutSummary.heartRate.peakBpm,
                          targetZone: workoutSummary.heartRate.targetZone,
                        }
                      : undefined,
                  } as ShareableWorkout);
                  setShowShareModal(true);
                }}
                variant="outline"
                className="flex-1 h-10 touch-manipulation"
              >
                <ShareIcon className="h-4 w-4 mr-1.5 text-primary" />
                {t('running.summary.shareButton')}
              </Button>
            <Button 
              onClick={handleSaveRunSummary}
              disabled={isSavingRun}
              className="flex-1 h-10 touch-manipulation bg-gradient-to-r from-primary to-accent text-black font-semibold disabled:opacity-60"
            >
                {isSavingRun ? t('running.summary.savingButton', { defaultValue: 'Saving…' }) : t('running.summary.saveButton')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Share Modal for Run Summary */}
      <ShareWorkoutModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setWorkoutToShare(null);
        }}
        workout={workoutToShare}
      />
      
      {/* STEP 2: Analyze with Arnold Dialog - dark overlay, no X, only two options */}
      <Dialog open={showAnalyzePrompt} onOpenChange={setShowAnalyzePrompt}>
        <DialogContent 
          hideClose 
          overlayClassName="bg-black"
          className="sm:max-w-sm max-w-[calc(100vw-2rem)] bg-background border-2 border-primary/30 shadow-2xl p-4 sm:p-6"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader className="text-center space-y-3 pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center shadow-glow">
              <Trophy className="h-8 w-8 text-gold" />
            </div>
            <DialogTitle className="text-xl font-bold text-center text-foreground">
              {t('running.summary.analysisPrompt.title')}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {runTitle}
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <p className="text-center text-sm font-medium text-foreground">
              {t('running.summary.analysisPrompt.body')}
            </p>
            
            <div className="flex flex-col gap-2">
              <Button 
                onClick={handleAnalyzeWithArnold}
                className="w-full gap-2 h-11 font-semibold bg-gradient-primary text-primary-foreground hover:opacity-90"
              >
                <MessageCircle className="h-4 w-4" />
                {t('running.summary.analysisPrompt.analyzeWithCoach', { coach: profile?.ai_coach_name || 'Arnold' })}
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleSkipAnalysis}
                className="w-full h-11 font-semibold border-2"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Battery optimization prompt - Android only, for reliable background tracking */}
      {showBatteryPrompt && createPortal(
        <div 
          className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/70 p-4"
          style={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <div 
            className="bg-background border border-border rounded-xl p-5 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">{t('running.active.batteryPrompt.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('running.active.batteryPrompt.body')}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={async () => {
                  setShowBatteryPrompt(false);
                  try {
                    const { BatteryOptimization } = await import('@capawesome-team/capacitor-android-battery-optimization');
                    await BatteryOptimization.openBatteryOptimizationSettings();
                  } catch (_) {}
                }}
                className="w-full"
              >
                {t('common.openSettings')}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowBatteryPrompt(false);
                  localStorage.setItem('straiv_battery_prompt_dismissed', 'true');
                }}
                className="w-full"
              >
                {t('common.maybeLater')}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog - must appear above Run Summary (z-99999) */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent 
          className="bg-background border-border z-[100000]"
          overlayClassName="z-[100000]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{t('running.summary.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('running.summary.deleteDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="touch-manipulation min-h-[44px]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteRun}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 touch-manipulation min-h-[44px]"
            >
              {t('running.summary.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {showExpandedCombined && createPortal(
        <div
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">{runTitle || t('running.summary.expandedMapFallbackTitle')}</div>
              <div className="text-xs text-muted-foreground">
                {workoutSummary?.distance || 0} km • {formatTime(elapsedSeconds)} • Route + HR
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedCombined(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col gap-3 px-2 pb-2">
            {routeLine.length > 1 && (
              <div className="flex-1 min-h-[40%] rounded-lg overflow-hidden border border-border/30">
                <PlatformMap
                  key="expanded-combined-map"
                  center={routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null}
                  routeSegments={coloredRouteSegmentsForSummary.length > 0 ? undefined : routeSegmentsForMap}
                  coloredRouteSegments={coloredRouteSegmentsForSummary.length > 0 ? coloredRouteSegmentsForSummary : undefined}
                  showUserMarker={false}
                  showRoute
                  showStartFinishMarkers
                  showKmMarkers
                  fitToRoute
                  className="w-full h-full"
                  darkMode
                  interactive
                  zoom={14}
                />
              </div>
            )}
            {workoutSummary?.heartRate ? (
              <div className="shrink-0 space-y-3 rounded-lg border border-border/30 bg-muted/10 p-3 overflow-y-auto max-h-[45%]">
                <RouteHrEffortChart
                  routePoints={routePoints}
                  hrSamples={workoutSummary.heartRate.samples ?? []}
                  zones={workoutSummary.heartRate.zones}
                />
                <HeartRateZoneChart
                  zones={workoutSummary.heartRate.zones}
                  buckets={workoutSummary.heartRate.buckets}
                  maxHr={workoutSummary.heartRate.maxHr}
                  maxHrSource={workoutSummary.heartRate.maxHrSource}
                  avgBpm={workoutSummary.heartRate.avgBpm}
                  peakBpm={workoutSummary.heartRate.peakBpm}
                  targetZone={workoutSummary.heartRate.targetZone}
                />
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}

      {showExpandedHr && workoutSummary?.heartRate && createPortal(
        <div
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">Heart rate zones</div>
              <div className="text-xs text-muted-foreground">
                Avg {workoutSummary.heartRate.avgBpm ?? '—'} bpm • Peak {workoutSummary.heartRate.peakBpm ?? '—'} bpm
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedHr(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
            <HeartRateZoneChart
              zones={workoutSummary.heartRate.zones}
              buckets={workoutSummary.heartRate.buckets}
              maxHr={workoutSummary.heartRate.maxHr}
              maxHrSource={workoutSummary.heartRate.maxHrSource}
              avgBpm={workoutSummary.heartRate.avgBpm}
              peakBpm={workoutSummary.heartRate.peakBpm}
              targetZone={workoutSummary.heartRate.targetZone}
              className="text-sm"
            />
          </div>
        </div>,
        document.body
      )}

      {/* Expanded Map - full-screen overlay, MUST be above Run Summary (z-99999) */}
      {showExpandedMap && createPortal(
        <div 
          className="fixed inset-0 z-[100002] flex flex-col bg-background"
          style={{ 
            top: 0, left: 0, right: 0, bottom: 0,
            paddingTop: SAFE_AREA.top,
            paddingBottom: SAFE_AREA.bottom,
            paddingLeft: SAFE_AREA.left,
            paddingRight: SAFE_AREA.right,
          }}
        >
          {/* Header with close */}
          <div className="shrink-0 flex items-center justify-between py-3 px-2">
            <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
              <div className="text-sm font-semibold">{runTitle || t('running.summary.expandedMapFallbackTitle')}</div>
              <div className="text-xs text-muted-foreground">
                {workoutSummary?.distance || 0} km • {formatTime(elapsedSeconds)}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowExpandedMap(false)}
              className="min-w-[44px] min-h-[44px] shrink-0 touch-manipulation"
              aria-label={t('running.summary.closeMapAria')}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          {/* Map - full height, scrollable/zoomable */}
          <div className="flex-1 min-h-0 relative">
            {routeLine.length > 1 && (
              <PlatformMap
                key="expanded-map"
                center={routeLine.length > 0 ? routeLine[Math.floor(routeLine.length / 2)] : null}
                routePoints={routeLine}
                routeSegments={routeSegmentsForMap}
                showUserMarker={false}
                showRoute={true}
                showStartFinishMarkers={true}
                showKmMarkers={true}
                fitToRoute={true}
                className="w-full h-full"
                darkMode={true}
                interactive={true}
                zoom={14}
              />
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

